import type { SiteLayer } from '../../types/site';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import {
  GENERATION_IMPACT_HASH_VERSION,
  GENERATION_IMPACT_SED_CONTRACT_VERSION,
  GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION,
  GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION,
  GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
  GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
  GENERATION_SIZING_STUDY_MODEL_VERSION,
  GENERATION_SIZING_STUDY_SCHEMA_VERSION,
  type GenerationImpactSnapshot,
  type GenerationImpactSedAuthoritySnapshot,
  type GenerationLayoutExhibitCadFeatureProvenance,
  type GenerationLayoutExhibitCadGeoreference,
  type GenerationLayoutExhibitCadSource,
  type GenerationLayoutExhibitEngineeringSchedule,
  type GenerationSizingStudySnapshot,
  type GenerationLayoutExhibitFit,
  type GenerationLayoutExhibitOverlayFeature,
  type GenerationLayoutExhibitSnapshot,
  type TestFitAuthorityRef,
} from '../../types/generationImpact';
import { materializeSiteBoundaries } from '../siteBoundaryGeometry';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import {
  requireValidGenerationImpactScenarioIntegrity,
  validateGenerationImpactScenarioIntegrity,
} from './generationImpactScenarioIntegrity';
import { buildGenerationParcelTestFit } from './generationLayoutGeometry';
import { SQUARE_METRES_PER_ACRE } from './generationTestFit';
import { buildGenerationSizingStudySnapshot } from './generationSizingStudy';
import {
  projectGenerationEquipmentExhibitBasis,
  validateGenerationEquipmentBasis,
} from './generationEquipmentCatalog';
import {
  generationProjectDutyCompleteness,
  generationProjectDutyGasPressureCheck,
  generationProjectDutySpatialSummary,
  projectGenerationProjectDutyExhibitBasis,
  validateGenerationProjectDutyBasis,
} from './generationProjectDuty';
import { materializeGenerationImpactAuthorityParcels } from './boundaryAuthority';
import { resolveGenerationImpactSedAuthority } from './sedAuthority';

/**
 * Full-report bodies are hash-addressed in object storage. Two MiB leaves
 * ample headroom for the rest of a report while retaining normal CAD test-fit
 * drawings in full. The guard is all-or-nothing; it never keeps an arbitrary
 * first-N prefix of the canonical overlay set.
 */
export const GENERATION_LAYOUT_EXHIBIT_MAX_OVERLAY_BYTES = 2 * 1024 * 1024;

export interface BuildGenerationLayoutExhibitSnapshotInput {
  impact: GenerationImpactSnapshot;
  /**
   * When present, this immutable SED authority is the exclusive source for
   * layout layers and parcel geometry. Mutable live site layers/boundaries are
   * ignored so a report cannot mix revisions.
   */
  authoritySnapshot?: GenerationImpactSedAuthoritySnapshot;
  /**
   * Compact enqueue-time Test Fit authority. When supplied, its SED identity is
   * hash-bound into the exhibit alongside the scenario/layout/boundary tuple.
   */
  testFitAuthorityRef?: TestFitAuthorityRef;
  siteLayers?: readonly SiteLayer[];
  siteLayersRevision?: number;
  siteLayersCanonicalVersion?: 1;
  siteBoundaries?: readonly SiteBoundaryParcel[];
  siteBoundary?: readonly { lat: number; lng: number }[];
  siteArea?: number;
  sitePerimeter?: number;
  siteCentroid?: { lat: number; lng: number };
  fallbackAnchor: { lat: number; lng: number };
  /** Test-only or deployment-policy override; production callers omit it. */
  maxOverlayBytes?: number;
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function approximatelyEqual(left: number, right: number): boolean {
  const scale = Math.max(1, Math.abs(left), Math.abs(right));
  return Math.abs(left - right) <= scale * 1e-8;
}

function jsonClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function finiteAnchor(
  preferred: { lat: number; lng: number } | undefined,
  fallback: { lat: number; lng: number },
): { lat: number; lng: number } {
  if (preferred && Number.isFinite(preferred.lat) && Number.isFinite(preferred.lng)) {
    return { lat: preferred.lat, lng: preferred.lng };
  }
  if (!Number.isFinite(fallback.lat) || !Number.isFinite(fallback.lng)) {
    throw new RangeError('Generation exhibit fallback anchor must contain finite latitude and longitude.');
  }
  return { lat: fallback.lat, lng: fallback.lng };
}

function stringAttribute(
  attributes: SiteLayer['attributes'],
  key: string,
): string | undefined {
  const value = attributes?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function numberAttribute(
  attributes: SiteLayer['attributes'],
  key: string,
): number | undefined {
  const value = attributes?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function cadGeoreference(
  attributes: SiteLayer['attributes'],
): GenerationLayoutExhibitCadGeoreference | undefined {
  const anchorLat = numberAttribute(attributes, 'cadAnchorLat');
  const anchorLng = numberAttribute(attributes, 'cadAnchorLng');
  const originX = numberAttribute(attributes, 'cadOriginX');
  const originY = numberAttribute(attributes, 'cadOriginY');
  const metersPerUnit = numberAttribute(attributes, 'cadMetersPerUnit');
  const rotationDeg = numberAttribute(attributes, 'cadRotationDeg');
  if (
    anchorLat == null
    || anchorLng == null
    || originX == null
    || originY == null
    || metersPerUnit == null
    || rotationDeg == null
  ) {
    return undefined;
  }
  return {
    anchorLat,
    anchorLng,
    originX,
    originY,
    metersPerUnit,
    rotationDeg,
    ...(stringAttribute(attributes, 'cadGeoreferenceSource')
      ? { source: stringAttribute(attributes, 'cadGeoreferenceSource') }
      : {}),
    ...(stringAttribute(attributes, 'cadCrsCode')
      ? { crsCode: stringAttribute(attributes, 'cadCrsCode') }
      : {}),
    ...(stringAttribute(attributes, 'cadCrsName')
      ? { crsName: stringAttribute(attributes, 'cadCrsName') }
      : {}),
  };
}

function cadFeatureProvenance(
  layer: SiteLayer,
): GenerationLayoutExhibitCadFeatureProvenance | undefined {
  if (layer.source !== 'cad-import') return undefined;
  const sourceFile = stringAttribute(layer.attributes, 'cadSourceFile');
  const sourceSha256 = stringAttribute(layer.attributes, 'cadSourceSha256');
  if (!sourceFile || !sourceSha256 || !/^[a-f\d]{64}$/i.test(sourceSha256)) return undefined;
  const georeference = cadGeoreference(layer.attributes);
  return {
    sourceFile,
    sourceSha256,
    ...(stringAttribute(layer.attributes, 'cadDwgVersion')
      ? { dwgVersion: stringAttribute(layer.attributes, 'cadDwgVersion') }
      : {}),
    ...(stringAttribute(layer.attributes, 'cadEntityHandle')
      ? { entityHandle: stringAttribute(layer.attributes, 'cadEntityHandle') }
      : {}),
    ...(stringAttribute(layer.attributes, 'cadEntityType')
      ? { entityType: stringAttribute(layer.attributes, 'cadEntityType') }
      : {}),
    ...(stringAttribute(layer.attributes, 'cadLayer')
      ? { layer: stringAttribute(layer.attributes, 'cadLayer') }
      : {}),
    ...(georeference ? { georeference } : {}),
  };
}

function invalidGeometryReason(layer: SiteLayer): string | null {
  const requiredCoordinates = layer.geometry.type === 'point'
    ? 1
    : layer.geometry.type === 'line'
      ? 2
      : 3;
  if (layer.geometry.coordinates.length < requiredCoordinates) {
    return `${layer.id} has ${layer.geometry.coordinates.length} coordinate(s); ${layer.geometry.type} requires at least ${requiredCoordinates}`;
  }
  const invalidIndex = layer.geometry.coordinates.findIndex(
    point => !Number.isFinite(point?.lat) || !Number.isFinite(point?.lng),
  );
  return invalidIndex >= 0
    ? `${layer.id} contains a non-finite coordinate at index ${invalidIndex}`
    : null;
}

function compactOverlayFeature(layer: SiteLayer): GenerationLayoutExhibitOverlayFeature {
  const cad = cadFeatureProvenance(layer);
  return {
    id: layer.id,
    name: layer.name,
    category: layer.category,
    source: layer.source,
    geometry: {
      type: layer.geometry.type,
      coordinates: layer.geometry.coordinates.map(point => ({ lat: point.lat, lng: point.lng })),
    },
    ...(layer.color ? { color: layer.color } : {}),
    ...(layer.contentHash ? { contentHash: layer.contentHash } : {}),
    importedAt: layer.importedAt,
    ...(layer.updatedAt ? { updatedAt: layer.updatedAt } : {}),
    ...(cad ? { cad } : {}),
  };
}

function aggregateCadSources(
  layers: readonly SiteLayer[],
): GenerationLayoutExhibitCadSource[] {
  const grouped = new Map<string, {
    sourceFile: string;
    sourceSha256: string;
    dwgVersion?: string;
    featureCount: number;
    layerNames: Set<string>;
    georeference?: GenerationLayoutExhibitCadGeoreference;
  }>();
  for (const layer of layers) {
    const cad = cadFeatureProvenance(layer);
    if (!cad) continue;
    const existing = grouped.get(cad.sourceSha256);
    if (existing) {
      existing.featureCount += 1;
      if (cad.layer) existing.layerNames.add(cad.layer);
      continue;
    }
    grouped.set(cad.sourceSha256, {
      sourceFile: cad.sourceFile,
      sourceSha256: cad.sourceSha256,
      ...(cad.dwgVersion ? { dwgVersion: cad.dwgVersion } : {}),
      featureCount: 1,
      layerNames: new Set(cad.layer ? [cad.layer] : []),
      ...(cad.georeference ? { georeference: cad.georeference } : {}),
    });
  }
  return [...grouped.values()]
    .sort((left, right) => compareText(left.sourceSha256, right.sourceSha256))
    .map(source => ({
      sourceFile: source.sourceFile,
      sourceSha256: source.sourceSha256,
      ...(source.dwgVersion ? { dwgVersion: source.dwgVersion } : {}),
      featureCount: source.featureCount,
      layerNames: [...source.layerNames].sort(compareText),
      ...(source.georeference ? { georeference: jsonClone(source.georeference) } : {}),
    }));
}

function engineeringSchedule(
  impact: GenerationImpactSnapshot,
  includeSizingStudy = true,
  /**
   * Exhibits frozen before the contingency-granularity curve existed carry a
   * sizing study without it. Rebuild without the curve so cross-validation
   * against generationImpactAtGen stays exact for those revisions.
   */
  includeContingencyGranularity = true,
): GenerationLayoutExhibitEngineeringSchedule {
  const input = impact.scenario.input;
  const result = impact.scenario.result;
  const generationBlockCount = input.units.reduce(
    (total, unit) => total + (Number.isInteger(unit.count) && unit.count > 0 ? unit.count : 0),
    0,
  );
  const isoGrossNameplateMW = input.units.reduce(
    (total, unit) => total + unit.count * unit.isoGrossMWPerUnit,
    0,
  );
  const siteGrossMW = result.unitResults.reduce(
    (total, unit) => total + unit.count * unit.siteGrossMWPerUnit,
    0,
  );
  const nearestReceptor = [...impact.noise.receptors]
    .sort((left, right) => (
      left.distanceM - right.distanceM
      || compareText(left.receptorId, right.receptorId)
    ))[0] ?? null;
  const equipmentBasis = projectGenerationEquipmentExhibitBasis(input);
  const projectDutyBasis = projectGenerationProjectDutyExhibitBasis(input);
  return {
    fleet: {
      technologyGroupCount: input.units.length,
      generationBlockCount,
      isoGrossNameplateMW,
      siteGrossMW,
    },
    capacity: jsonClone(result.capacity),
    gas: jsonClone(result.gas),
    water: jsonClone(result.water),
    emissions: jsonClone(result.emissions),
    footprint: jsonClone(result.footprint),
    nearestReceptor: nearestReceptor ? jsonClone(nearestReceptor) : null,
    lighting: jsonClone(result.lighting),
    gaps: [...result.gaps],
    warnings: [...result.warnings],
    noiseCaveats: [...impact.noise.caveats],
    ...(equipmentBasis ? { equipmentBasis } : {}),
    ...(projectDutyBasis ? { projectDutyBasis } : {}),
    ...(includeSizingStudy
      ? { sizingStudy: sizingStudyForSchedule(impact, includeContingencyGranularity) }
      : {}),
  };
}

function sizingStudyForSchedule(
  impact: GenerationImpactSnapshot,
  includeContingencyGranularity: boolean,
): GenerationSizingStudySnapshot {
  const study = buildGenerationSizingStudySnapshot(impact.scenario);
  if (includeContingencyGranularity) return study;
  const { contingencyGranularity: _omitted, ...withoutCurve } = study;
  return withoutCurve;
}

function validateGenerationEquipmentExhibitBasis(value: unknown, errors: string[]): void {
  const path = 'exhibit.engineering.equipmentBasis';
  const projection = requiredRecord(value, path, errors);
  if (!projection) return;
  const basisErrors = validateGenerationEquipmentBasis(projection.selection);
  errors.push(...basisErrors.map(error => `${path}.selection ${error}.`));
  if (!requiredString(
    projection.currentDutyFingerprint,
    `${path}.currentDutyFingerprint`,
    errors,
  ) || !/^[a-f\d]{64}$/i.test(String(projection.currentDutyFingerprint))) {
    errors.push(`${path}.currentDutyFingerprint must be a SHA-256 digest.`);
  }
  requiredEnum(
    projection.alignmentStatus,
    ['aligned', 'modified'],
    `${path}.alignmentStatus`,
    errors,
  );
  requiredStringArray(projection.alignmentNotes, `${path}.alignmentNotes`, errors);
  const selection = isRecord(projection.selection) ? projection.selection : null;
  if (selection && typeof selection.appliedDutyFingerprint === 'string') {
    const same = selection.appliedDutyFingerprint === projection.currentDutyFingerprint;
    if (projection.alignmentStatus === 'aligned' && !same) {
      errors.push(`${path}.alignmentStatus is aligned but the duty fingerprints differ.`);
    }
    if (projection.alignmentStatus === 'modified' && same) {
      errors.push(`${path}.alignmentStatus is modified but the duty fingerprints match.`);
    }
  }
}

function validateGenerationProjectDutyExhibitBasis(value: unknown, errors: string[]): void {
  const path = 'exhibit.engineering.projectDutyBasis';
  const projection = requiredRecord(value, path, errors);
  if (!projection) return;
  const basisErrors = validateGenerationProjectDutyBasis(projection.basis);
  errors.push(...basisErrors.map(error => `${path}.basis ${error}.`));
  if (!requiredString(
    projection.currentDutyFingerprint,
    `${path}.currentDutyFingerprint`,
    errors,
  ) || !/^[a-f\d]{64}$/i.test(String(projection.currentDutyFingerprint))) {
    errors.push(`${path}.currentDutyFingerprint must be a SHA-256 digest.`);
  }
  requiredEnum(
    projection.alignmentStatus,
    ['aligned', 'modified'],
    `${path}.alignmentStatus`,
    errors,
  );
  requiredStringArray(projection.alignmentNotes, `${path}.alignmentNotes`, errors);

  const basis = isRecord(projection.basis) ? projection.basis : null;
  if (!basis || basisErrors.length > 0) return;
  try {
    const typedBasis = basis as unknown as Parameters<
      typeof generationProjectDutyCompleteness
    >[0];
    const projectedCompleteness = isRecord(projection.completeness)
      ? projection.completeness
      : null;
    const projectedStatuses = projectedCompleteness && isRecord(projectedCompleteness.statuses)
      ? projectedCompleteness.statuses
      : null;
    const ccsEnabled = projectedStatuses?.ccsIntegration !== 'not-applicable';
    if (canonicalJson(projection.completeness) !== canonicalJson(
      generationProjectDutyCompleteness(typedBasis, ccsEnabled),
    )) {
      errors.push(`${path}.completeness does not match the project duty dossier.`);
    }
    if (canonicalJson(projection.gasPressureCheck) !== canonicalJson(
      generationProjectDutyGasPressureCheck(typedBasis),
    )) {
      errors.push(`${path}.gasPressureCheck does not match the project duty dossier.`);
    }
    if (canonicalJson(projection.spatialSummary) !== canonicalJson(
      generationProjectDutySpatialSummary(
        typedBasis.installedReservations,
        ccsEnabled,
      ),
    )) {
      errors.push(`${path}.spatialSummary does not match the installed reservations.`);
    }
    const appliedFingerprint = basis.appliedDutyFingerprint;
    if (
      typeof appliedFingerprint === 'string'
      && projection.alignmentStatus === 'aligned'
      && projection.currentDutyFingerprint !== appliedFingerprint
    ) {
      errors.push(`${path}.alignmentStatus is aligned but the duty fingerprints differ.`);
    }
    if (
      typeof appliedFingerprint === 'string'
      && projection.alignmentStatus === 'modified'
      && projection.currentDutyFingerprint === appliedFingerprint
    ) {
      errors.push(`${path}.alignmentStatus is modified but the duty fingerprints match.`);
    }
  } catch (error) {
    errors.push(`${path} cannot be projected: ${(error as Error).message}`);
  }
}

function frozenFit(
  impact: GenerationImpactSnapshot,
  parcels: readonly SiteBoundaryParcel[],
  fallbackAnchor: { lat: number; lng: number },
): GenerationLayoutExhibitFit {
  const generationBlockCount = Math.max(1, impact.scenario.input.units.reduce(
    (total, unit) => total + (Number.isInteger(unit.count) && unit.count > 0 ? unit.count : 0),
    0,
  ));
  const result = buildGenerationParcelTestFit({
    parcels,
    layout: impact.scenario.input.layout,
    fallbackAnchor,
    generationBlockCount,
    footprint: impact.scenario.result.footprint,
    installedReservations: impact.scenario.input.projectDutyBasis?.installedReservations,
  });
  // The geometry builder adds authored campus components after the packer
  // plan. Freeze their true polygon areas in the same ledger as the emitted
  // components; leave the packer geometry, orientation and status untouched.
  const plannedIds = new Set(result.plan?.rectangles.map(rectangle => rectangle.id) ?? []);
  const additionalComponents = result.components.filter(component => !plannedIds.has(component.rectangle.id));
  const additionalArea = additionalComponents.reduce((sum, component) => sum + component.rectangle.areaAcres, 0);
  const frozenTotalArea = result.plan ? result.plan.totalComponentAreaAcres + additionalArea : 0;
  const additionalLifecycleArea = (lifecycle: 'permanent' | 'route-reservation' | 'construction') => additionalComponents
    .filter(component => component.rectangle.lifecycle === lifecycle)
    .reduce((sum, component) => sum + component.rectangle.areaAcres, 0);
  const parcel = result.parcel
    ? {
        id: result.parcel.id,
        ...(result.parcel.label ? { label: result.parcel.label } : {}),
        areaAcres: result.parcel.area,
        perimeterKm: result.parcel.perimeter,
        centroid: { ...result.parcel.centroid },
        ...(result.parcel.intendedUse ? { intendedUse: result.parcel.intendedUse } : {}),
        polygon: result.parcel.polygon.map(point => ({ ...point })),
        geometryHash: hashCanonicalJson({
          id: result.parcel.id,
          polygon: result.parcel.polygon,
          area: result.parcel.area,
          perimeter: result.parcel.perimeter,
          centroid: result.parcel.centroid,
        }),
      }
    : null;
  return {
    ...(result.plan ? { fitModelVersion: result.plan.modelVersion } : {}),
    status: result.status,
    error: result.error,
    caveats: [...result.caveats],
    layout: jsonClone(result.layout),
    anchor: { ...result.anchor },
    parcel,
    envelope: result.envelope
      ? {
          center: { ...result.envelope.center },
          widthM: result.envelope.widthM,
          heightM: result.envelope.heightM,
          parcelPolygonM: result.envelope.parcelPolygonM.map(point => ({ ...point })),
        }
      : null,
    plan: result.plan
      ? {
          status: result.plan.status,
          orientation: result.plan.orientation,
          usableEnvelope: { ...result.plan.usableEnvelope },
          packingEnvelope: { ...result.plan.packingEnvelope },
          requiredEnvelope: { ...result.plan.requiredEnvelope },
          totalComponentAreaAcres: additionalComponents.length > 0 ? frozenTotalArea : result.plan.totalComponentAreaAcres,
          componentAreaMarginAcres: additionalComponents.length > 0
            ? result.plan.packingEnvelope.areaAcres - frozenTotalArea : result.plan.componentAreaMarginAcres,
          componentAreaUtilizationPct: additionalComponents.length > 0
            ? frozenTotalArea / result.plan.packingEnvelope.areaAcres * 100 : result.plan.componentAreaUtilizationPct,
          widthOverflowM: result.plan.widthOverflowM,
          heightOverflowM: result.plan.heightOverflowM,
          orientationEvaluations: result.plan.orientationEvaluations.map(evaluation => ({ ...evaluation })),
          ...(result.plan.packingScheme != null
            ? { packingScheme: result.plan.packingScheme }
            : {}),
          ...(result.plan.schemeEvaluations
            ? { schemeEvaluations: result.plan.schemeEvaluations.map(evaluation => ({ ...evaluation })) }
            : {}),
          reservationSummary: additionalComponents.length > 0 ? {
            permanentEquipmentAcres: result.plan.reservationSummary.permanentEquipmentAcres + additionalLifecycleArea('permanent'),
            routeReservationAcres: result.plan.reservationSummary.routeReservationAcres + additionalLifecycleArea('route-reservation'),
            constructionLaydownAcres: result.plan.reservationSummary.constructionLaydownAcres + additionalLifecycleArea('construction'),
            totalReservedAcres: result.plan.reservationSummary.totalReservedAcres + additionalArea,
          } : { ...result.plan.reservationSummary },
        }
      : null,
    components: result.components.map(component => ({
      id: component.rectangle.id,
      label: component.rectangle.label,
      kind: component.rectangle.kind,
      ...(component.rectangle.blockNumber != null
        ? { blockNumber: component.rectangle.blockNumber }
        : {}),
      areaAcres: component.rectangle.areaAcres,
      xM: component.rectangle.xM,
      yM: component.rectangle.yM,
      widthM: component.rectangle.widthM,
      heightM: component.rectangle.heightM,
      polygonM: component.rectangle.polygonM.map(point => ({ ...point })),
      fitsWithinEnvelope: component.rectangle.fitsWithinEnvelope,
      coordinates: component.coordinates.map(point => ({ ...point })),
      containedAcres: component.containedAcres,
      outsideAcres: component.outsideAcres,
      parcelEdgeClearanceM: component.parcelEdgeClearanceM,
      meetsParcelEdgeSetback: component.meetsParcelEdgeSetback,
      dimensionBasis: component.rectangle.dimensionBasis,
      lifecycle: component.rectangle.lifecycle,
      ...(component.exclusionOverlapAcres !== undefined
        ? { exclusionOverlapAcres: component.exclusionOverlapAcres }
        : {}),
    })),
    containedAcres: result.containedAcres,
    outsideAcres: result.outsideAcres,
    containmentPct: result.containmentPct,
    minimumParcelEdgeClearanceM: result.minimumParcelEdgeClearanceM,
    parcelEdgeSetbackSatisfied: result.parcelEdgeSetbackSatisfied,
    ...(result.usable ? { usable: { ...result.usable } } : {}),
    ...(result.exclusionOverlapAcres !== undefined
      ? { exclusionOverlapAcres: result.exclusionOverlapAcres }
      : {}),
    ...(result.placementOffsetM ? { placementOffsetM: { ...result.placementOffsetM } } : {}),
  };
}

/** Project canonical site state into one immutable, renderer-independent exhibit. */
export function buildGenerationLayoutExhibitSnapshot(
  input: BuildGenerationLayoutExhibitSnapshotInput,
): GenerationLayoutExhibitSnapshot {
  requireValidGenerationImpactScenarioIntegrity(input.impact.scenario);
  const maxBytes = input.maxOverlayBytes ?? GENERATION_LAYOUT_EXHIBIT_MAX_OVERLAY_BYTES;
  if (!Number.isSafeInteger(maxBytes) || maxBytes <= 0) {
    throw new RangeError(`Generation exhibit overlay byte limit must be a positive safe integer; received ${maxBytes}.`);
  }

  const frozenAuthority = input.authoritySnapshot
    ? resolveGenerationImpactSedAuthority({
        generationImpactAuthoritySnapshot: input.authoritySnapshot,
      })
    : null;
  if (frozenAuthority && frozenAuthority.status !== 'valid') {
    throw new Error(
      frozenAuthority.status === 'invalid'
        ? `Cannot build generation layout exhibit: ${frozenAuthority.reason}`
        : 'Cannot build generation layout exhibit: frozen authority is absent',
    );
  }
  const authority = frozenAuthority?.status === 'valid' ? frozenAuthority.snapshot : null;
  if (input.testFitAuthorityRef && !authority) {
    throw new Error(
      'Cannot build generation layout exhibit: Test Fit authority was supplied without a valid frozen SED authority',
    );
  }
  if (authority) {
    const scenario = input.impact.scenario;
    const frozenScenario = authority.scenarioSnapshot;
    if (
      !frozenScenario.promotedAt
      || scenario.id !== frozenScenario.id
      || scenario.name !== frozenScenario.name
      || scenario.updatedAt !== frozenScenario.updatedAt
      || scenario.inputHash !== frozenScenario.inputHash
      || scenario.resultHash !== frozenScenario.resultHash
      || scenario.result.modelVersion !== frozenScenario.result.modelVersion
      || (scenario.promotedAt ?? undefined) !== (frozenScenario.promotedAt ?? undefined)
    ) {
      throw new Error(
        'Cannot build generation layout exhibit: impact snapshot does not match the frozen SED authority scenario',
      );
    }
    const ref = input.testFitAuthorityRef;
    if (ref && (
      ref.scenarioId !== authority.scenarioSnapshot.id
      || ref.scenarioUpdatedAt !== authority.scenarioSnapshot.updatedAt
      || ref.inputHash !== authority.provenance.inputHash
      || ref.resultHash !== authority.provenance.resultHash
      || ref.modelVersion !== authority.provenance.modelVersion
      || ref.promotedAt !== authority.scenarioSnapshot.promotedAt
      || ref.layoutPublicationId !== authority.provenance.layoutPublicationId
      || ref.layoutVersion !== authority.provenance.layoutVersion
      || ref.layoutHash !== authority.provenance.layoutHash
      || ref.layoutPublishedAt !== authority.provenance.layoutPublishedAt
      || ref.boundaryHash !== authority.provenance.parcelBoundaryHash
    )) {
      throw new Error(
        'Cannot build generation layout exhibit: compact Test Fit authority does not match the frozen SED authority',
      );
    }
  }
  const parcels = authority
    ? materializeGenerationImpactAuthorityParcels(
        authority.parcelBoundary,
        {
          targetParcelId: input.impact.scenario.input.layout?.targetParcelId,
          preferredAnchor: input.impact.scenario.input.layoutAnchor,
        },
      )
    : materializeSiteBoundaries({
        siteBoundaries: input.siteBoundaries?.map(parcel => jsonClone(parcel)),
        siteBoundary: input.siteBoundary?.map(point => ({ ...point })),
        siteArea: input.siteArea,
        sitePerimeter: input.sitePerimeter,
        siteCentroid: input.siteCentroid ? { ...input.siteCentroid } : undefined,
      });
  const fallbackAnchor = finiteAnchor(
    input.impact.scenario.input.layoutAnchor,
    authority
      ? parcels.find(parcel => (
          parcel.id === input.impact.scenario.input.layout?.targetParcelId
        ))?.centroid ?? parcels[0]?.centroid ?? input.fallbackAnchor
      : input.siteCentroid ?? input.fallbackAnchor,
  );
  const fit = frozenFit(input.impact, parcels, fallbackAnchor);
  const sourceLayers = authority
    ? authority.layoutPublication.layers
    : input.siteLayers ?? [];
  const visibleLayers = [...sourceLayers]
    .filter(layer => layer.visible)
    .sort((left, right) => compareText(left.id, right.id) || compareText(left.name, right.name));
  const invalidOverlayEntries = visibleLayers.flatMap(layer => {
    const reasons: string[] = [];
    const geometryReason = invalidGeometryReason(layer);
    if (geometryReason) reasons.push(geometryReason);
    if (layer.source === 'cad-import' && !cadFeatureProvenance(layer)) {
      reasons.push(`${layer.id} is a CAD-derived layer without a source filename and full SHA-256 digest`);
    }
    return reasons.length > 0 ? [{ layerId: layer.id, reason: reasons.join('; ') }] : [];
  });
  const invalidLayerIds = invalidOverlayEntries.map(entry => entry.layerId);
  const candidateFeatures = invalidOverlayEntries.length > 0
    ? []
    : visibleLayers.map(compactOverlayFeature);
  const cadSources = aggregateCadSources(visibleLayers);
  const serializedBytes = invalidOverlayEntries.length > 0
    ? 0
    : new TextEncoder().encode(canonicalJson({
        features: candidateFeatures,
        cadSources,
      })).byteLength;
  const overlayIncluded = invalidOverlayEntries.length === 0 && serializedBytes <= maxBytes;
  const sourceLayerIds = visibleLayers.map(layer => layer.id);
  const warnings = [
    ...(fit.error ? [`Generation layout is indeterminate: ${fit.error}`] : []),
    ...(authority
      ? ['Overlay scope is limited to the frozen published-layout authority; mutable live site and CAD layers were not consulted.']
      : []),
    ...(invalidOverlayEntries.length > 0
      ? [`Visible CAD/site overlay geometry is omitted in full because canonical geometry or CAD source provenance is invalid: ${invalidOverlayEntries.map(entry => entry.reason).join('; ')}. No layers were partially retained.`]
      : !overlayIncluded
      ? [`Visible CAD/site overlay geometry is omitted in full because its ${serializedBytes.toLocaleString('en-US')} serialized bytes exceed the explicit ${maxBytes.toLocaleString('en-US')}-byte exhibit ceiling; no layers were partially retained.`]
      : []),
  ];
  const semanticSnapshot: Omit<GenerationLayoutExhibitSnapshot, 'contentHash'> = {
    schemaVersion: GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION,
    modelVersion: GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION,
    capturedAt: input.impact.capturedAt,
    scenario: {
      id: input.impact.scenario.id,
      name: input.impact.scenario.name,
      inputHash: input.impact.scenario.inputHash,
      resultHash: input.impact.scenario.resultHash,
      hashVersion: GENERATION_IMPACT_HASH_VERSION,
      modelVersion: input.impact.scenario.result.modelVersion,
      updatedAt: input.impact.scenario.updatedAt,
    },
    engineering: engineeringSchedule(input.impact),
    provenance: {
      siteLayersRevision: authority ? null : input.siteLayersRevision ?? null,
      siteLayersCanonicalVersion: authority ? null : input.siteLayersCanonicalVersion ?? null,
      sourceLayerCount: sourceLayers.length,
      visibleLayerCount: visibleLayers.length,
      selectedParcelId: fit.parcel?.id ?? null,
      selectedParcelHash: fit.parcel?.geometryHash ?? null,
      ...(authority
        ? {
            frozenAuthority: {
              contractVersion: authority.contractVersion,
              scenarioId: authority.scenarioSnapshot.id,
              scenarioUpdatedAt: authority.scenarioSnapshot.updatedAt,
              inputHash: authority.provenance.inputHash,
              resultHash: authority.provenance.resultHash,
              modelVersion: authority.provenance.modelVersion,
              promotedAt: authority.scenarioSnapshot.promotedAt!,
              layoutPublicationId: authority.provenance.layoutPublicationId,
              layoutVersion: authority.provenance.layoutVersion,
              layoutHash: authority.provenance.layoutHash,
              layoutPublishedAt: authority.provenance.layoutPublishedAt,
              boundaryHash: authority.provenance.parcelBoundaryHash,
              boundaryAreaAcres: authority.provenance.parcelBoundaryAreaAcres,
              ...(input.testFitAuthorityRef
                ? {
                    sedId: input.testFitAuthorityRef.sedId,
                    sedVersion: input.testFitAuthorityRef.sedVersion,
                  }
                : {}),
            },
          }
        : {}),
    },
    fit,
    overlays: {
      status: invalidOverlayEntries.length > 0
        ? 'omitted-invalid'
        : overlayIncluded
          ? 'included'
          : 'omitted-too-large',
      maxBytes,
      serializedBytes,
      sourceLayerIds,
      includedLayerIds: overlayIncluded ? [...sourceLayerIds] : [],
      omittedLayerIds: overlayIncluded ? [] : [...sourceLayerIds],
      invalidLayerIds,
      totalVertices: visibleLayers.reduce(
        (total, layer) => total + layer.geometry.coordinates.length,
        0,
      ),
      features: overlayIncluded ? candidateFeatures : [],
      cadSources,
    },
    warnings,
  };
  const snapshot: GenerationLayoutExhibitSnapshot = {
    ...semanticSnapshot,
    contentHash: hashCanonicalJson(semanticSnapshot),
  };
  return requireValidGenerationLayoutExhibitSnapshot(snapshot, input.impact);
}

export interface GenerationLayoutExhibitValidationResult {
  valid: boolean;
  errors: string[];
}

function sortedUnique(values: readonly string[]): boolean {
  return values.every((value, index) => (
    (index === 0 || compareText(values[index - 1]!, value) < 0)
    && values.indexOf(value) === index
  ));
}

function sameStrings(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function collectNonFiniteNumbers(
  value: unknown,
  path: string,
  output: string[],
): void {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) output.push(`${path} is not finite`);
    return;
  }
  if (value == null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((entry, index) => collectNonFiniteNumbers(entry, `${path}[${index}]`, output));
    return;
  }
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    collectNonFiniteNumbers(entry, `${path}.${key}`, output);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function requiredRecord(
  value: unknown,
  path: string,
  errors: string[],
): Record<string, unknown> | null {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object.`);
    return null;
  }
  return value;
}

function requiredArray(
  value: unknown,
  path: string,
  errors: string[],
): unknown[] | null {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array.`);
    return null;
  }
  return value;
}

function requiredString(
  value: unknown,
  path: string,
  errors: string[],
): value is string {
  if (typeof value !== 'string' || value.length === 0) {
    errors.push(`${path} must be a non-empty string.`);
    return false;
  }
  return true;
}

function optionalString(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (value !== undefined && typeof value !== 'string') {
    errors.push(`${path} must be a string when present.`);
  }
}

function nullableString(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (value !== null && typeof value !== 'string') {
    errors.push(`${path} must be a string or null.`);
  }
}

function requiredFiniteNumber(
  value: unknown,
  path: string,
  errors: string[],
): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.push(`${path} must be a finite number.`);
    return false;
  }
  return true;
}

function requiredNonnegativeSafeInteger(
  value: unknown,
  path: string,
  errors: string[],
): value is number {
  if (!requiredFiniteNumber(value, path, errors)) return false;
  if (!Number.isSafeInteger(value) || value < 0) {
    errors.push(`${path} must be a nonnegative safe integer.`);
    return false;
  }
  return true;
}

function nullableFiniteNumber(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) {
    errors.push(`${path} must be a finite number or null.`);
  }
}

function requiredBoolean(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (typeof value !== 'boolean') errors.push(`${path} must be a boolean.`);
}

function nullableBoolean(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (value !== null && typeof value !== 'boolean') {
    errors.push(`${path} must be a boolean or null.`);
  }
}

function requiredEnum(
  value: unknown,
  allowed: readonly string[],
  path: string,
  errors: string[],
): void {
  if (typeof value !== 'string' || !allowed.includes(value)) {
    errors.push(`${path} must be one of: ${allowed.join(', ')}.`);
  }
}

function requiredStringArray(
  value: unknown,
  path: string,
  errors: string[],
): string[] | null {
  const entries = requiredArray(value, path, errors);
  if (!entries) return null;
  entries.forEach((entry, index) => {
    if (typeof entry !== 'string') errors.push(`${path}[${index}] must be a string.`);
  });
  return entries.filter((entry): entry is string => typeof entry === 'string');
}

function validateLatLng(
  value: unknown,
  path: string,
  errors: string[],
): void {
  const point = requiredRecord(value, path, errors);
  if (!point) return;
  const latitude = point.lat;
  const longitude = point.lng;
  const latitudeValid = requiredFiniteNumber(latitude, `${path}.lat`, errors);
  const longitudeValid = requiredFiniteNumber(longitude, `${path}.lng`, errors);
  if (latitudeValid && (latitude < -90 || latitude > 90)) {
    errors.push(`${path}.lat must be between -90 and 90 degrees.`);
  }
  if (longitudeValid && (longitude < -180 || longitude > 180)) {
    errors.push(`${path}.lng must be between -180 and 180 degrees.`);
  }
}

function validateLocalPoint(
  value: unknown,
  path: string,
  errors: string[],
): void {
  const point = requiredRecord(value, path, errors);
  if (!point) return;
  requiredFiniteNumber(point.xM, `${path}.xM`, errors);
  requiredFiniteNumber(point.yM, `${path}.yM`, errors);
}

function validateLatLngArray(
  value: unknown,
  path: string,
  minimumLength: number,
  errors: string[],
): unknown[] | null {
  const coordinates = requiredArray(value, path, errors);
  if (!coordinates) return null;
  if (coordinates.length < minimumLength) {
    errors.push(`${path} must contain at least ${minimumLength} coordinate(s).`);
  }
  coordinates.forEach((coordinate, index) => validateLatLng(
    coordinate,
    `${path}[${index}]`,
    errors,
  ));
  return coordinates;
}

function validateLocalPointArray(
  value: unknown,
  path: string,
  minimumLength: number,
  errors: string[],
): unknown[] | null {
  const coordinates = requiredArray(value, path, errors);
  if (!coordinates) return null;
  if (coordinates.length < minimumLength) {
    errors.push(`${path} must contain at least ${minimumLength} point(s).`);
  }
  coordinates.forEach((coordinate, index) => validateLocalPoint(
    coordinate,
    `${path}[${index}]`,
    errors,
  ));
  return coordinates;
}

function validateNumericRecord(
  value: unknown,
  path: string,
  fields: readonly string[],
  errors: string[],
): Record<string, unknown> | null {
  const record = requiredRecord(value, path, errors);
  if (!record) return null;
  fields.forEach(field => requiredFiniteNumber(record[field], `${path}.${field}`, errors));
  return record;
}

function validateGenerationSizingStudy(value: unknown, errors: string[]): void {
  const path = 'exhibit.engineering.sizingStudy';
  const study = requiredRecord(value, path, errors);
  if (!study) return;
  if (study.schemaVersion !== GENERATION_SIZING_STUDY_SCHEMA_VERSION) {
    errors.push(`${path}.schemaVersion must be ${GENERATION_SIZING_STUDY_SCHEMA_VERSION}.`);
  }
  if (study.modelVersion !== GENERATION_SIZING_STUDY_MODEL_VERSION) {
    errors.push(`${path}.modelVersion must be ${GENERATION_SIZING_STUDY_MODEL_VERSION}.`);
  }
  requiredEnum(study.status, ['available', 'unavailable'], `${path}.status`, errors);
  nullableString(study.reason, `${path}.reason`, errors);
  if (requiredFiniteNumber(study.incrementMW, `${path}.incrementMW`, errors) && study.incrementMW <= 0) {
    errors.push(`${path}.incrementMW must be greater than zero.`);
  }
  requiredStringArray(study.warnings, `${path}.warnings`, errors);

  const basis = requiredRecord(study.basis, `${path}.basis`, errors);
  if (basis) {
    for (const digestField of ['baselineInputHash', 'baselineResultHash'] as const) {
      if (!requiredString(basis[digestField], `${path}.basis.${digestField}`, errors)
        || !/^[a-f\d]{64}$/i.test(basis[digestField] as string)) {
        errors.push(`${path}.basis.${digestField} must be a SHA-256 digest.`);
      }
    }
    requiredEnum(
      basis.selectionCriterion,
      ['lowest-iso-nameplate-passing-n-1-plus-reserve'],
      `${path}.basis.selectionCriterion`,
      errors,
    );
    const counts = requiredArray(basis.candidateBlockCounts, `${path}.basis.candidateBlockCounts`, errors);
    const validCounts = counts?.filter((count): count is number => {
      if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 2) {
        errors.push(`${path}.basis.candidateBlockCounts must contain integers of at least two.`);
        return false;
      }
      return true;
    }) ?? [];
    if (counts && (
      validCounts.length !== counts.length
      || new Set(validCounts).size !== validCounts.length
      || validCounts.some((count, index) => index > 0 && count <= validCounts[index - 1]!)
    )) {
      errors.push(`${path}.basis.candidateBlockCounts must be sorted and unique.`);
    }
  }

  if (study.uniformSizing !== null) {
    const sizing = validateNumericRecord(study.uniformSizing, `${path}.uniformSizing`, [
      'blockCount',
      'onlineBlocksAfterContingency',
      'configuredGrossMWPerBlock',
      'netDeliveryFactor',
      'requiredLoadGrossMWPerBlock',
      'requiredReserveGrossMWPerBlock',
      'suggestedLoadGrossMWPerBlock',
      'suggestedReserveGrossMWPerBlock',
      'incrementMW',
    ], errors);
    if (sizing) {
      const blockCountValid = requiredNonnegativeSafeInteger(sizing.blockCount, `${path}.uniformSizing.blockCount`, errors);
      const onlineCountValid = requiredNonnegativeSafeInteger(sizing.onlineBlocksAfterContingency, `${path}.uniformSizing.onlineBlocksAfterContingency`, errors);
      if (blockCountValid && Number(sizing.blockCount) < 2) {
        errors.push(`${path}.uniformSizing.blockCount must be at least two.`);
      }
      if (blockCountValid && onlineCountValid && Number(sizing.onlineBlocksAfterContingency) !== Number(sizing.blockCount) - 1) {
        errors.push(`${path}.uniformSizing.onlineBlocksAfterContingency must equal blockCount minus one.`);
      }
      for (const field of [
        'configuredGrossMWPerBlock',
        'netDeliveryFactor',
        'requiredLoadGrossMWPerBlock',
        'requiredReserveGrossMWPerBlock',
        'suggestedLoadGrossMWPerBlock',
        'suggestedReserveGrossMWPerBlock',
        'incrementMW',
      ] as const) {
        if (typeof sizing[field] === 'number' && sizing[field] <= 0) {
          errors.push(`${path}.uniformSizing.${field} must be greater than zero.`);
        }
      }
      requiredBoolean(sizing.configuredMeetsLoad, `${path}.uniformSizing.configuredMeetsLoad`, errors);
      requiredBoolean(sizing.configuredMeetsReserve, `${path}.uniformSizing.configuredMeetsReserve`, errors);
    }
  }

  // Additive and optional: exhibits frozen before the granularity curve existed
  // omit the key entirely and stay valid at this model version.
  if (study.contingencyGranularity != null) {
    const curvePath = `${path}.contingencyGranularity`;
    const curve = validateNumericRecord(study.contingencyGranularity, curvePath, [
      'basisRequirementMW',
      'configuredBlockCount',
      'netDeliveryFactor',
      'incrementMW',
    ], errors);
    if (curve) {
      requiredEnum(curve.basis, ['critical-load', 'reserve'], `${curvePath}.basis`, errors);
      for (const field of ['basisRequirementMW', 'netDeliveryFactor', 'incrementMW'] as const) {
        if (typeof curve[field] === 'number' && curve[field] <= 0) {
          errors.push(`${curvePath}.${field} must be greater than zero.`);
        }
      }
      if (requiredNonnegativeSafeInteger(curve.configuredBlockCount, `${curvePath}.configuredBlockCount`, errors)
        && Number(curve.configuredBlockCount) < 2) {
        errors.push(`${curvePath}.configuredBlockCount must be at least two.`);
      }
      const curveRows = requiredArray(curve.rows, `${curvePath}.rows`, errors);
      const seenBlockCounts: number[] = [];
      curveRows?.forEach((row, index) => {
        const rowPath = `${curvePath}.rows[${index}]`;
        const record = validateNumericRecord(row, rowPath, [
          'blockCount',
          'onlineBlocksAfterContingency',
          'requiredGrossMWPerBlock',
          'requiredInstalledGrossMW',
          'installedGrossMW',
          'incrementRoundingPenaltyMW',
          'firmAfterContingencyMW',
          'requiredInstalledRelativeToFinest',
        ], errors);
        if (!record) return;
        const blockCountValid = requiredNonnegativeSafeInteger(record.blockCount, `${rowPath}.blockCount`, errors);
        if (blockCountValid && Number(record.blockCount) < 2) {
          errors.push(`${rowPath}.blockCount must be at least two.`);
        }
        if (blockCountValid) seenBlockCounts.push(Number(record.blockCount));
        if (Number(record.onlineBlocksAfterContingency) !== Number(record.blockCount) - 1) {
          errors.push(`${rowPath}.onlineBlocksAfterContingency must equal blockCount minus one.`);
        }
        if (Number(record.incrementRoundingPenaltyMW) < 0) {
          errors.push(`${rowPath}.incrementRoundingPenaltyMW must not be negative.`);
        }
        if (Number(record.requiredInstalledGrossMW) <= 0 || Number(record.installedGrossMW) <= 0) {
          errors.push(`${rowPath} installed nameplate must be greater than zero.`);
        }
      });
      if (seenBlockCounts.some((count, index) => index > 0 && count <= seenBlockCounts[index - 1]!)) {
        errors.push(`${curvePath}.rows must be sorted by ascending blockCount.`);
      }
    }
  }

  const options = requiredArray(study.options, `${path}.options`, errors);
  const optionRecords: Record<string, unknown>[] = [];
  options?.forEach((option, index) => {
    const optionPath = `${path}.options[${index}]`;
    const record = validateNumericRecord(option, optionPath, [
      'blockCount',
      'onlineBlocksAfterContingency',
      'isoGrossMWPerBlock',
      'isoGrossNameplateMW',
      'siteNetMWPerBlock',
      'normalNetMW',
      'nMinusOneNetMW',
      'criticalLoadMW',
      'reserveRequiredMW',
      'nMinusOneMarginMW',
      'nMinusOneReserveMarginMW',
      'peakGasDthPerDay',
      'generationAndCcsConsumptionGalPerDay',
      'capturedCo2TonnesPerYear',
      'residualCo2TonnesPerYear',
      'footprintAcres',
    ], errors);
    if (!record) return;
    optionRecords.push(record);
    requiredString(record.id, `${optionPath}.id`, errors);
    requiredString(record.label, `${optionPath}.label`, errors);
    requiredEnum(record.basis, ['configured', 'reserve-rounded'], `${optionPath}.basis`, errors);
    requiredEnum(record.nMinusOneVerdict, ['pass', 'fail', 'indeterminate'], `${optionPath}.nMinusOneVerdict`, errors);
    requiredEnum(record.nMinusOneReserveVerdict, ['pass', 'fail', 'indeterminate'], `${optionPath}.nMinusOneReserveVerdict`, errors);
    const blockCountValid = requiredNonnegativeSafeInteger(record.blockCount, `${optionPath}.blockCount`, errors);
    requiredNonnegativeSafeInteger(record.onlineBlocksAfterContingency, `${optionPath}.onlineBlocksAfterContingency`, errors);
    if (blockCountValid && Number(record.blockCount) < 2) {
      errors.push(`${optionPath}.blockCount must be at least two.`);
    }
    for (const field of [
      'isoGrossMWPerBlock',
      'isoGrossNameplateMW',
      'siteNetMWPerBlock',
      'normalNetMW',
      'nMinusOneNetMW',
      'criticalLoadMW',
      'reserveRequiredMW',
    ] as const) {
      if (typeof record[field] === 'number' && record[field] <= 0) {
        errors.push(`${optionPath}.${field} must be greater than zero.`);
      }
    }
    for (const field of [
      'peakGasDthPerDay',
      'generationAndCcsConsumptionGalPerDay',
      'capturedCo2TonnesPerYear',
      'residualCo2TonnesPerYear',
      'footprintAcres',
    ] as const) {
      if (typeof record[field] === 'number' && record[field] < 0) {
        errors.push(`${optionPath}.${field} must be nonnegative.`);
      }
    }
    nullableFiniteNumber(record.campusConsumptionGalPerDay, `${optionPath}.campusConsumptionGalPerDay`, errors);
    nullableFiniteNumber(record.probabilityLoadServed, `${optionPath}.probabilityLoadServed`, errors);
    if (
      typeof record.probabilityLoadServed === 'number'
      && (record.probabilityLoadServed < 0 || record.probabilityLoadServed > 1)
    ) {
      errors.push(`${optionPath}.probabilityLoadServed must be between zero and one.`);
    }
    requiredBoolean(record.ccsEnabled, `${optionPath}.ccsEnabled`, errors);
    for (const digestField of ['inputHash', 'resultHash'] as const) {
      if (!requiredString(record[digestField], `${optionPath}.${digestField}`, errors)
        || !/^[a-f\d]{64}$/i.test(record[digestField] as string)) {
        errors.push(`${optionPath}.${digestField} must be a SHA-256 digest.`);
      }
    }
  });

  const optionIds = optionRecords.flatMap(option => typeof option.id === 'string' ? [option.id] : []);
  if (optionIds.length !== new Set(optionIds).size) {
    errors.push(`${path}.options ids must be unique.`);
  }
  const configuredOptions = optionRecords.filter(option => option.basis === 'configured');
  if (study.status === 'available' && (
    configuredOptions.length !== 1
    || optionRecords[0]?.basis !== 'configured'
  )) {
    errors.push(`${path}.options must contain exactly one configured basis as the first row.`);
  }
  const ccsModes = new Set(optionRecords.map(option => option.ccsEnabled));
  if (ccsModes.size > 1) {
    errors.push(`${path}.options must hold the promoted CCS mode constant.`);
  }
  optionRecords.forEach((option, index) => {
    const optionPath = `${path}.options[${index}]`;
    const blockCount = Number(option.blockCount);
    const onlineBlocks = Number(option.onlineBlocksAfterContingency);
    const grossPerBlock = Number(option.isoGrossMWPerBlock);
    const netPerBlock = Number(option.siteNetMWPerBlock);
    const nMinusOneNet = Number(option.nMinusOneNetMW);
    const criticalLoad = Number(option.criticalLoadMW);
    const reserveRequired = Number(option.reserveRequiredMW);
    if (
      !approximatelyEqual(Number(option.isoGrossNameplateMW), blockCount * grossPerBlock)
      || !approximatelyEqual(Number(option.normalNetMW), blockCount * netPerBlock)
      || !approximatelyEqual(nMinusOneNet, onlineBlocks * netPerBlock)
      || !approximatelyEqual(Number(option.nMinusOneMarginMW), nMinusOneNet - criticalLoad)
      || !approximatelyEqual(Number(option.nMinusOneReserveMarginMW), nMinusOneNet - reserveRequired)
      || onlineBlocks !== blockCount - 1
    ) {
      errors.push(`${optionPath} capacity arithmetic is internally inconsistent.`);
    }
  });
  if (basis && configuredOptions[0]) {
    if (
      configuredOptions[0].inputHash !== basis.baselineInputHash
      || configuredOptions[0].resultHash !== basis.baselineResultHash
    ) {
      errors.push(`${path} configured option hashes must match the frozen baseline hashes.`);
    }
  }
  nullableString(study.lowestNameplatePassingReserveOptionId, `${path}.lowestNameplatePassingReserveOptionId`, errors);
  const selectedId = typeof study.lowestNameplatePassingReserveOptionId === 'string'
    ? study.lowestNameplatePassingReserveOptionId
    : null;
  if (selectedId) {
    const selected = optionRecords.find(option => option.id === selectedId);
    if (!selected || selected.nMinusOneReserveVerdict !== 'pass') {
      errors.push(`${path}.lowestNameplatePassingReserveOptionId must identify a reserve-passing option.`);
    } else {
      const lowest = optionRecords
        .filter(option => option.nMinusOneReserveVerdict === 'pass')
        .sort((left, right) => (
          Number(left.isoGrossNameplateMW) - Number(right.isoGrossNameplateMW)
          || Number(left.isoGrossMWPerBlock) - Number(right.isoGrossMWPerBlock)
          || Number(left.blockCount) - Number(right.blockCount)
          || String(left.id).localeCompare(String(right.id))
        ))[0];
      if (lowest?.id !== selectedId) {
        errors.push(`${path}.lowestNameplatePassingReserveOptionId is not the lowest-nameplate passing option.`);
      }
    }
  } else if (optionRecords.some(option => option.nMinusOneReserveVerdict === 'pass')) {
    errors.push(`${path}.lowestNameplatePassingReserveOptionId is required when a reserve-passing option exists.`);
  }

  if (study.status === 'available') {
    if (study.reason !== null || (options?.length ?? 0) === 0) {
      errors.push(`${path} available status requires null reason and at least one option.`);
    }
  } else if (study.status === 'unavailable') {
    if (typeof study.reason !== 'string' || study.reason.length === 0 || (options?.length ?? 0) !== 0 || selectedId !== null) {
      errors.push(`${path} unavailable status requires a reason, no options, and no selected option.`);
    }
  }
}

function validateEngineeringSchedule(
  value: unknown,
  errors: string[],
): void {
  const engineering = requiredRecord(value, 'exhibit.engineering', errors);
  if (!engineering) return;
  const fleet = validateNumericRecord(engineering.fleet, 'exhibit.engineering.fleet', [
    'technologyGroupCount',
    'generationBlockCount',
    'isoGrossNameplateMW',
    'siteGrossMW',
  ], errors);
  if (fleet) {
    requiredNonnegativeSafeInteger(
      fleet.technologyGroupCount,
      'exhibit.engineering.fleet.technologyGroupCount',
      errors,
    );
    requiredNonnegativeSafeInteger(
      fleet.generationBlockCount,
      'exhibit.engineering.fleet.generationBlockCount',
      errors,
    );
  }

  const capacity = validateNumericRecord(engineering.capacity, 'exhibit.engineering.capacity', [
    'normalNetMW',
    'largestContingencyMW',
    'nMinusOneNetMW',
    'criticalLoadMW',
    'reserveRequiredMW',
    'nMinusOneMarginMW',
    'nMinusOneReserveMarginMW',
  ], errors);
  if (capacity) {
    nullableString(
      capacity.largestContingencyGroupId,
      'exhibit.engineering.capacity.largestContingencyGroupId',
      errors,
    );
    requiredEnum(capacity.nMinusOneVerdict, ['pass', 'fail', 'indeterminate'], 'exhibit.engineering.capacity.nMinusOneVerdict', errors);
    requiredEnum(capacity.nMinusOneReserveVerdict, ['pass', 'fail', 'indeterminate'], 'exhibit.engineering.capacity.nMinusOneReserveVerdict', errors);
  }

  validateNumericRecord(engineering.gas, 'exhibit.engineering.gas', [
    'peakMmbtuPerHour',
    'peakDthPerDay',
    'annualMmbtu',
  ], errors);

  const water = requiredRecord(engineering.water, 'exhibit.engineering.water', errors);
  if (water) {
    ['generationWithdrawalGalPerDay', 'generationConsumptionGalPerDay', 'ccsWithdrawalGalPerDay', 'ccsConsumptionGalPerDay']
      .forEach(field => requiredFiniteNumber(water[field], `exhibit.engineering.water.${field}`, errors));
    ['dataCenterWithdrawalGalPerDay', 'dataCenterConsumptionGalPerDay', 'totalWithdrawalGalPerDay', 'totalConsumptionGalPerDay']
      .forEach(field => nullableFiniteNumber(water[field], `exhibit.engineering.water.${field}`, errors));
  }

  const emissions = requiredRecord(engineering.emissions, 'exhibit.engineering.emissions', errors);
  if (emissions) {
    ['uncontrolledCo2TonnesPerYear', 'capturedCo2TonnesPerYear', 'residualCo2TonnesPerYear']
      .forEach(field => requiredFiniteNumber(emissions[field], `exhibit.engineering.emissions.${field}`, errors));
    ['noxTonnesPerYear', 'coTonnesPerYear']
      .forEach(field => nullableFiniteNumber(emissions[field], `exhibit.engineering.emissions.${field}`, errors));
  }

  validateNumericRecord(engineering.footprint, 'exhibit.engineering.footprint', [
    'plantAcres',
    'coolingAcres',
    'switchyardAcres',
    'ccsAcres',
    'compressionAcres',
    'totalAcres',
  ], errors);

  if (engineering.nearestReceptor !== null) {
    const receptor = requiredRecord(
      engineering.nearestReceptor,
      'exhibit.engineering.nearestReceptor',
      errors,
    );
    if (receptor) {
      requiredString(receptor.receptorId, 'exhibit.engineering.nearestReceptor.receptorId', errors);
      requiredString(receptor.receptorName, 'exhibit.engineering.nearestReceptor.receptorName', errors);
      requiredFiniteNumber(receptor.distanceM, 'exhibit.engineering.nearestReceptor.distanceM', errors);
      nullableFiniteNumber(receptor.predictedDbA, 'exhibit.engineering.nearestReceptor.predictedDbA', errors);
      nullableFiniteNumber(receptor.thresholdDbA, 'exhibit.engineering.nearestReceptor.thresholdDbA', errors);
      nullableFiniteNumber(receptor.marginDb, 'exhibit.engineering.nearestReceptor.marginDb', errors);
      requiredEnum(receptor.verdict, ['pass', 'exceedance', 'indeterminate'], 'exhibit.engineering.nearestReceptor.verdict', errors);
      const components = requiredArray(
        receptor.components,
        'exhibit.engineering.nearestReceptor.components',
        errors,
      );
      components?.forEach((component, index) => {
        const path = `exhibit.engineering.nearestReceptor.components[${index}]`;
        const record = requiredRecord(component, path, errors);
        if (!record) return;
        requiredString(record.unitId, `${path}.unitId`, errors);
        requiredString(record.unitName, `${path}.unitName`, errors);
        nullableFiniteNumber(record.levelDbA, `${path}.levelDbA`, errors);
        requiredEnum(record.status, ['calculated', 'inside-reference-distance', 'missing-input'], `${path}.status`, errors);
        requiredEnum(record.basis, ['sound-power', 'reference-spl', 'legacy-sound-power'], `${path}.basis`, errors);
      });
    }
  }

  const lighting = requiredRecord(engineering.lighting, 'exhibit.engineering.lighting', errors);
  if (lighting) {
    nullableFiniteNumber(lighting.installedPowerKW, 'exhibit.engineering.lighting.installedPowerKW', errors);
    nullableFiniteNumber(lighting.installedLumens, 'exhibit.engineering.lighting.installedLumens', errors);
    requiredStringArray(lighting.planningFlags, 'exhibit.engineering.lighting.planningFlags', errors);
  }
  requiredStringArray(engineering.gaps, 'exhibit.engineering.gaps', errors);
  requiredStringArray(engineering.warnings, 'exhibit.engineering.warnings', errors);
  requiredStringArray(engineering.noiseCaveats, 'exhibit.engineering.noiseCaveats', errors);
  if ('equipmentBasis' in engineering) {
    validateGenerationEquipmentExhibitBasis(engineering.equipmentBasis, errors);
  }
  if ('projectDutyBasis' in engineering) {
    validateGenerationProjectDutyExhibitBasis(engineering.projectDutyBasis, errors);
    const projectProjection = isRecord(engineering.projectDutyBasis)
      ? engineering.projectDutyBasis
      : null;
    const projectBasis = projectProjection && isRecord(projectProjection.basis)
      ? projectProjection.basis
      : null;
    const catalogBinding = projectBasis && isRecord(projectBasis.catalogBinding)
      ? projectBasis.catalogBinding
      : null;
    const equipmentProjection = isRecord(engineering.equipmentBasis)
      ? engineering.equipmentBasis
      : null;
    const equipmentSelection = equipmentProjection && isRecord(equipmentProjection.selection)
      ? equipmentProjection.selection
      : null;
    if (!equipmentSelection) {
      errors.push('exhibit.engineering.projectDutyBasis requires equipmentBasis.');
    } else if (catalogBinding && (
      catalogBinding.catalogId !== equipmentSelection.catalogId
      || catalogBinding.catalogAppliedDutyFingerprint
        !== equipmentSelection.appliedDutyFingerprint
    )) {
      errors.push('exhibit.engineering.projectDutyBasis catalog binding does not match equipmentBasis.');
    }
  }
  if ('sizingStudy' in engineering) {
    validateGenerationSizingStudy(engineering.sizingStudy, errors);
  }
}

function validateFit(value: unknown, errors: string[]): void {
  const fit = requiredRecord(value, 'exhibit.fit', errors);
  if (!fit) return;
  if (fit.fitModelVersion !== undefined) {
    requiredEnum(
      fit.fitModelVersion,
      // Additive: v3 (road-rows) was stamped at freeze but missing here, so a
      // frozen road-rows exhibit failed its own revalidation; v4 = Test-Fit
      // Studio (schemaVersion 2) geometry. Pre-existing v2 exhibits validate
      // unchanged.
      ['generation-test-fit-v2', 'generation-test-fit-v3', 'generation-test-fit-v4'],
      'exhibit.fit.fitModelVersion',
      errors,
    );
  }
  requiredEnum(fit.status, ['fits', 'partial', 'indeterminate'], 'exhibit.fit.status', errors);
  nullableString(fit.error, 'exhibit.fit.error', errors);
  requiredStringArray(fit.caveats, 'exhibit.fit.caveats', errors);

  const layout = requiredRecord(fit.layout, 'exhibit.fit.layout', errors);
  if (layout) {
    optionalString(layout.targetParcelId, 'exhibit.fit.layout.targetParcelId', errors);
    requiredFiniteNumber(layout.rotationDeg, 'exhibit.fit.layout.rotationDeg', errors);
    requiredEnum(layout.orientation, ['auto', 'blocks-along-width', 'blocks-along-height'], 'exhibit.fit.layout.orientation', errors);
    requiredFiniteNumber(layout.edgeSetbackM, 'exhibit.fit.layout.edgeSetbackM', errors);
    requiredFiniteNumber(layout.componentGapM, 'exhibit.fit.layout.componentGapM', errors);
    requiredFiniteNumber(layout.generationBlockAspectRatio, 'exhibit.fit.layout.generationBlockAspectRatio', errors);
  }
  validateLatLng(fit.anchor, 'exhibit.fit.anchor', errors);

  if (fit.parcel !== null) {
    const parcel = requiredRecord(fit.parcel, 'exhibit.fit.parcel', errors);
    if (parcel) {
      requiredString(parcel.id, 'exhibit.fit.parcel.id', errors);
      optionalString(parcel.label, 'exhibit.fit.parcel.label', errors);
      requiredFiniteNumber(parcel.areaAcres, 'exhibit.fit.parcel.areaAcres', errors);
      requiredFiniteNumber(parcel.perimeterKm, 'exhibit.fit.parcel.perimeterKm', errors);
      validateLatLng(parcel.centroid, 'exhibit.fit.parcel.centroid', errors);
      if (parcel.intendedUse !== undefined) {
        requiredEnum(parcel.intendedUse, ['datacenter', 'substation', 'water', 'buffer', 'access', 'storage', 'other'], 'exhibit.fit.parcel.intendedUse', errors);
      }
      validateLatLngArray(parcel.polygon, 'exhibit.fit.parcel.polygon', 3, errors);
      if (!requiredString(parcel.geometryHash, 'exhibit.fit.parcel.geometryHash', errors)
        || !/^[a-f\d]{64}$/i.test(parcel.geometryHash as string)) {
        errors.push('exhibit.fit.parcel.geometryHash must be a SHA-256 digest.');
      }
    }
  }

  if (fit.envelope !== null) {
    const envelope = requiredRecord(fit.envelope, 'exhibit.fit.envelope', errors);
    if (envelope) {
      validateLatLng(envelope.center, 'exhibit.fit.envelope.center', errors);
      requiredFiniteNumber(envelope.widthM, 'exhibit.fit.envelope.widthM', errors);
      requiredFiniteNumber(envelope.heightM, 'exhibit.fit.envelope.heightM', errors);
      validateLocalPointArray(envelope.parcelPolygonM, 'exhibit.fit.envelope.parcelPolygonM', 3, errors);
    }
  }

  if (fit.plan !== null) {
    const plan = requiredRecord(fit.plan, 'exhibit.fit.plan', errors);
    if (plan) {
      requiredEnum(plan.status, ['fit', 'overflow'], 'exhibit.fit.plan.status', errors);
      requiredEnum(plan.orientation, ['blocks-along-width', 'blocks-along-height'], 'exhibit.fit.plan.orientation', errors);
      for (const field of ['usableEnvelope', 'packingEnvelope', 'requiredEnvelope'] as const) {
        validateNumericRecord(plan[field], `exhibit.fit.plan.${field}`, ['widthM', 'heightM', 'areaAcres'], errors);
      }
      ['totalComponentAreaAcres', 'componentAreaMarginAcres', 'componentAreaUtilizationPct', 'widthOverflowM', 'heightOverflowM']
        .forEach(field => requiredFiniteNumber(plan[field], `exhibit.fit.plan.${field}`, errors));
      const evaluations = requiredArray(
        plan.orientationEvaluations,
        'exhibit.fit.plan.orientationEvaluations',
        errors,
      );
      evaluations?.forEach((evaluation, index) => {
        const path = `exhibit.fit.plan.orientationEvaluations[${index}]`;
        const record = requiredRecord(evaluation, path, errors);
        if (!record) return;
        requiredEnum(record.orientation, ['blocks-along-width', 'blocks-along-height'], `${path}.orientation`, errors);
        requiredBoolean(record.fits, `${path}.fits`, errors);
        ['requiredWidthM', 'requiredHeightM', 'widthOverflowM', 'heightOverflowM', 'boundingAreaAcres']
          .forEach(field => requiredFiniteNumber(record[field], `${path}.${field}`, errors));
      });
      if (plan.reservationSummary !== undefined) {
        validateNumericRecord(
          plan.reservationSummary,
          'exhibit.fit.plan.reservationSummary',
          [
            'permanentEquipmentAcres',
            'routeReservationAcres',
            'constructionLaydownAcres',
            'totalReservedAcres',
          ],
          errors,
        );
      }
    }
  }

  const components = requiredArray(fit.components, 'exhibit.fit.components', errors);
  components?.forEach((component, index) => {
    const path = `exhibit.fit.components[${index}]`;
    const record = requiredRecord(component, path, errors);
    if (!record) return;
    requiredString(record.id, `${path}.id`, errors);
    requiredString(record.label, `${path}.label`, errors);
    requiredEnum(record.kind, [
      'generation-block',
      'cooling',
      'switchyard',
      'ccs-capture',
      'ccs-compression',
      'natural-gas-route',
      'co2-export-route',
      'construction-laydown',
      // Studio kinds (schemaVersion 2) — additive so pre-studio frozen
      // exhibits keep validating unchanged.
      'access-road',
      'road',
      'parking',
      'building',
      'data-center',
      'generator',
      'custom',
    ], `${path}.kind`, errors);
    if (record.blockNumber !== undefined) requiredFiniteNumber(record.blockNumber, `${path}.blockNumber`, errors);
    ['areaAcres', 'xM', 'yM', 'widthM', 'heightM']
      .forEach(field => requiredFiniteNumber(record[field], `${path}.${field}`, errors));
    const polygon = validateLocalPointArray(record.polygonM, `${path}.polygonM`, 3, errors);
    const coordinates = validateLatLngArray(record.coordinates, `${path}.coordinates`, 3, errors);
    if (polygon && coordinates && polygon.length !== coordinates.length) {
      errors.push(`${path}.polygonM and ${path}.coordinates must contain the same number of vertices.`);
    }
    requiredBoolean(record.fitsWithinEnvelope, `${path}.fitsWithinEnvelope`, errors);
    nullableFiniteNumber(record.containedAcres, `${path}.containedAcres`, errors);
    nullableFiniteNumber(record.outsideAcres, `${path}.outsideAcres`, errors);
    nullableFiniteNumber(record.parcelEdgeClearanceM, `${path}.parcelEdgeClearanceM`, errors);
    nullableBoolean(record.meetsParcelEdgeSetback, `${path}.meetsParcelEdgeSetback`, errors);
    if (record.dimensionBasis !== undefined) {
      requiredEnum(record.dimensionBasis, [
        'planning-acreage',
        'project-installed-dimensions',
        'project-route-reservation',
        'project-construction-reservation',
        // Studio (schemaVersion 2) user-drawn/imported footprints — additive.
        'user-defined',
      ], `${path}.dimensionBasis`, errors);
    }
    if (record.lifecycle !== undefined) {
      requiredEnum(record.lifecycle, [
        'permanent',
        'route-reservation',
        'construction',
      ], `${path}.lifecycle`, errors);
    }
    if (fit.fitModelVersion === 'generation-test-fit-v2' && (
      record.dimensionBasis === undefined || record.lifecycle === undefined
    )) {
      errors.push(`${path} must retain dimensionBasis and lifecycle for generation-test-fit-v2.`);
    }
  });
  ['containedAcres', 'outsideAcres', 'containmentPct', 'minimumParcelEdgeClearanceM']
    .forEach(field => nullableFiniteNumber(fit[field], `exhibit.fit.${field}`, errors));
  nullableBoolean(fit.parcelEdgeSetbackSatisfied, 'exhibit.fit.parcelEdgeSetbackSatisfied', errors);
}

const OVERLAY_CATEGORIES = [
  'infrastructure.pipeline',
  'infrastructure.electrical',
  'infrastructure.fiber',
  'infrastructure.transport',
  'constraint.environmental',
  'concept.expansion',
  'concept.generation',
  'concept.ccs',
  'impact.noise',
  'impact.receptor',
  'other',
] as const;

const OVERLAY_SOURCES = [
  'kmz-import',
  'cad-import',
  'manual',
  'arcgis-import',
  'peeringdb-import',
  'landgate-import',
  'doc-derived',
  'scenario-derived',
] as const;

function validateCadGeoreference(value: unknown, path: string, errors: string[]): void {
  const georeference = requiredRecord(value, path, errors);
  if (!georeference) return;
  const anchorLat = georeference.anchorLat;
  const anchorLng = georeference.anchorLng;
  const latitudeValid = requiredFiniteNumber(anchorLat, `${path}.anchorLat`, errors);
  const longitudeValid = requiredFiniteNumber(anchorLng, `${path}.anchorLng`, errors);
  if (latitudeValid && (anchorLat < -90 || anchorLat > 90)) {
    errors.push(`${path}.anchorLat must be between -90 and 90 degrees.`);
  }
  if (longitudeValid && (anchorLng < -180 || anchorLng > 180)) {
    errors.push(`${path}.anchorLng must be between -180 and 180 degrees.`);
  }
  ['originX', 'originY', 'metersPerUnit', 'rotationDeg']
    .forEach(field => requiredFiniteNumber(georeference[field], `${path}.${field}`, errors));
  optionalString(georeference.source, `${path}.source`, errors);
  optionalString(georeference.crsCode, `${path}.crsCode`, errors);
  optionalString(georeference.crsName, `${path}.crsName`, errors);
}

function validateCadProvenance(value: unknown, path: string, errors: string[]): void {
  const cad = requiredRecord(value, path, errors);
  if (!cad) return;
  requiredString(cad.sourceFile, `${path}.sourceFile`, errors);
  if (!requiredString(cad.sourceSha256, `${path}.sourceSha256`, errors)
    || !/^[a-f\d]{64}$/i.test(cad.sourceSha256 as string)) {
    errors.push(`${path}.sourceSha256 must be a full SHA-256 digest.`);
  }
  optionalString(cad.dwgVersion, `${path}.dwgVersion`, errors);
  optionalString(cad.entityHandle, `${path}.entityHandle`, errors);
  optionalString(cad.entityType, `${path}.entityType`, errors);
  optionalString(cad.layer, `${path}.layer`, errors);
  if (cad.georeference !== undefined) validateCadGeoreference(cad.georeference, `${path}.georeference`, errors);
}

function validateOverlayFeature(
  value: unknown,
  index: number,
  errors: string[],
): void {
  const path = `exhibit.overlays.features[${index}]`;
  const feature = requiredRecord(value, path, errors);
  if (!feature) return;
  requiredString(feature.id, `${path}.id`, errors);
  requiredString(feature.name, `${path}.name`, errors);
  requiredEnum(feature.category, OVERLAY_CATEGORIES, `${path}.category`, errors);
  requiredEnum(feature.source, OVERLAY_SOURCES, `${path}.source`, errors);
  if (
    requiredString(feature.importedAt, `${path}.importedAt`, errors)
    && !Number.isFinite(Date.parse(feature.importedAt as string))
  ) {
    errors.push(`${path}.importedAt must be a valid timestamp.`);
  }
  optionalString(feature.updatedAt, `${path}.updatedAt`, errors);
  if (typeof feature.updatedAt === 'string' && !Number.isFinite(Date.parse(feature.updatedAt))) {
    errors.push(`${path}.updatedAt must be a valid timestamp.`);
  }
  optionalString(feature.color, `${path}.color`, errors);
  optionalString(feature.contentHash, `${path}.contentHash`, errors);

  const geometry = requiredRecord(feature.geometry, `${path}.geometry`, errors);
  if (geometry) {
    requiredEnum(geometry.type, ['point', 'line', 'polygon'], `${path}.geometry.type`, errors);
    const minimumLength = geometry.type === 'point' ? 1 : geometry.type === 'line' ? 2 : 3;
    validateLatLngArray(geometry.coordinates, `${path}.geometry.coordinates`, minimumLength, errors);
  }
  if (feature.source === 'cad-import' && feature.cad === undefined) {
    errors.push(`${path}.cad is required for a CAD-derived overlay feature.`);
  }
  if (feature.source !== 'cad-import' && feature.cad !== undefined) {
    errors.push(`${path}.cad is only valid for a CAD-derived overlay feature.`);
  }
  if (feature.cad !== undefined) validateCadProvenance(feature.cad, `${path}.cad`, errors);
}

function validateCadSource(value: unknown, index: number, errors: string[]): void {
  const path = `exhibit.overlays.cadSources[${index}]`;
  const source = requiredRecord(value, path, errors);
  if (!source) return;
  requiredString(source.sourceFile, `${path}.sourceFile`, errors);
  if (!requiredString(source.sourceSha256, `${path}.sourceSha256`, errors)
    || !/^[a-f\d]{64}$/i.test(source.sourceSha256 as string)) {
    errors.push(`${path}.sourceSha256 must be a full SHA-256 digest.`);
  }
  optionalString(source.dwgVersion, `${path}.dwgVersion`, errors);
  if (
    requiredFiniteNumber(source.featureCount, `${path}.featureCount`, errors)
    && (!Number.isSafeInteger(source.featureCount) || source.featureCount <= 0)
  ) {
    errors.push(`${path}.featureCount must be a positive safe integer.`);
  }
  const layerNames = requiredStringArray(source.layerNames, `${path}.layerNames`, errors);
  if (layerNames && !sortedUnique(layerNames)) {
    errors.push(`${path}.layerNames must be sorted and unique.`);
  }
  if (source.georeference !== undefined) {
    validateCadGeoreference(source.georeference, `${path}.georeference`, errors);
  }
}

function reconstructCadSourcesFromFeatures(
  features: readonly unknown[],
  errors: string[],
): GenerationLayoutExhibitCadSource[] {
  const grouped = new Map<string, {
    sourceFile: string;
    sourceSha256: string;
    dwgVersion?: string;
    featureCount: number;
    layerNames: Set<string>;
    georeference?: GenerationLayoutExhibitCadGeoreference;
  }>();
  features.forEach((value, index) => {
    if (!isRecord(value) || value.source !== 'cad-import' || !isRecord(value.cad)) return;
    const cad = value.cad;
    if (
      typeof cad.sourceFile !== 'string'
      || typeof cad.sourceSha256 !== 'string'
      || !/^[a-f\d]{64}$/i.test(cad.sourceSha256)
    ) return;
    const dwgVersion = typeof cad.dwgVersion === 'string' && cad.dwgVersion.length > 0
      ? cad.dwgVersion
      : undefined;
    const layer = typeof cad.layer === 'string' && cad.layer.length > 0 ? cad.layer : undefined;
    const georeference = isRecord(cad.georeference)
      ? cad.georeference as unknown as GenerationLayoutExhibitCadGeoreference
      : undefined;
    const existing = grouped.get(cad.sourceSha256);
    if (existing) {
      existing.featureCount += 1;
      if (layer) existing.layerNames.add(layer);
      if (canonicalJson({
        sourceFile: existing.sourceFile,
        dwgVersion: existing.dwgVersion,
        georeference: existing.georeference,
      }) !== canonicalJson({ sourceFile: cad.sourceFile, dwgVersion, georeference })) {
        errors.push(
          `exhibit.overlays.features[${index}].cad conflicts with another feature carrying source SHA-256 ${cad.sourceSha256}.`,
        );
      }
      return;
    }
    grouped.set(cad.sourceSha256, {
      sourceFile: cad.sourceFile,
      sourceSha256: cad.sourceSha256,
      ...(dwgVersion ? { dwgVersion } : {}),
      featureCount: 1,
      layerNames: new Set(layer ? [layer] : []),
      ...(georeference ? { georeference } : {}),
    });
  });
  return [...grouped.values()]
    .sort((left, right) => compareText(left.sourceSha256, right.sourceSha256))
    .map(source => ({
      sourceFile: source.sourceFile,
      sourceSha256: source.sourceSha256,
      ...(source.dwgVersion ? { dwgVersion: source.dwgVersion } : {}),
      featureCount: source.featureCount,
      layerNames: [...source.layerNames].sort(compareText),
      ...(source.georeference ? { georeference: source.georeference } : {}),
    }));
}

/**
 * PRESENCE-GATED validation for the frozen max-power study.
 *
 * CROSS-FIELD INVARIANTS ONLY — this deliberately does NOT re-derive the study.
 * Re-derivation is what `engineering` does, and a drift there silently drops the
 * whole exhibit; a study that depends on parcels, layout and polygon clipping
 * would make that failure mode routine. Here a genuine inconsistency is an
 * ERROR (loud), and a legacy exhibit without the field validates byte-identically
 * because the gate is false.
 */
function validateMaxPowerStudy(source: Record<string, unknown>, errors: string[]): void {
  if (source.maxPowerStudy === undefined) return; // legacy exhibit — unchanged

  const study = requiredRecord(source.maxPowerStudy, 'exhibit.maxPowerStudy', errors);
  if (!study) return;

  if (study.modelVersion !== GENERATION_MAX_POWER_STUDY_MODEL_VERSION) {
    errors.push('exhibit.maxPowerStudy.modelVersion is unsupported.');
  }
  if (study.schemaVersion !== GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION) {
    errors.push('exhibit.maxPowerStudy.schemaVersion is unsupported.');
  }
  if (study.status !== 'available' && study.status !== 'unavailable') {
    errors.push('exhibit.maxPowerStudy.status must be available or unavailable.');
  }
  // The bound quality can never claim exactness — that invariant is the whole
  // honesty contract of the feature, so it is enforced at the persistence edge.
  if (study.status === 'available'
    && !['bounded-by-documented-constraints', 'upper-bound-undocumented-constraints', 'search-ceiling-reached']
      .includes(study.boundQuality as string)) {
    errors.push('exhibit.maxPowerStudy.boundQuality must be a documented bound-quality value.');
  }

  const basis = requiredRecord(study.basis, 'exhibit.maxPowerStudy.basis', errors);
  const scenario = isRecord(source.scenario) ? source.scenario : null;
  if (basis && scenario) {
    // The study must describe the SAME scenario the exhibit froze; otherwise a
    // report could quote a capacity the rest of the exhibit contradicts.
    if (basis.baselineInputHash !== scenario.inputHash) {
      errors.push('exhibit.maxPowerStudy.basis.baselineInputHash does not match the frozen scenario.');
    }
    if (basis.baselineResultHash !== scenario.resultHash) {
      errors.push('exhibit.maxPowerStudy.basis.baselineResultHash does not match the frozen scenario.');
    }
  }
}

function validateTopLevelStructure(source: Record<string, unknown>, errors: string[]): void {
  validateEngineeringSchedule(source.engineering, errors);
  validateFit(source.fit, errors);
  requiredStringArray(source.warnings, 'exhibit.warnings', errors);
  validateMaxPowerStudy(source, errors);

  const provenance = requiredRecord(source.provenance, 'exhibit.provenance', errors);
  if (provenance) {
    nullableFiniteNumber(provenance.siteLayersRevision, 'exhibit.provenance.siteLayersRevision', errors);
    if (provenance.siteLayersCanonicalVersion !== null && provenance.siteLayersCanonicalVersion !== 1) {
      errors.push('exhibit.provenance.siteLayersCanonicalVersion must be 1 or null.');
    }
    requiredNonnegativeSafeInteger(provenance.sourceLayerCount, 'exhibit.provenance.sourceLayerCount', errors);
    requiredNonnegativeSafeInteger(provenance.visibleLayerCount, 'exhibit.provenance.visibleLayerCount', errors);
    nullableString(provenance.selectedParcelId, 'exhibit.provenance.selectedParcelId', errors);
    if (provenance.selectedParcelHash !== null) {
      if (!requiredString(provenance.selectedParcelHash, 'exhibit.provenance.selectedParcelHash', errors)
        || !/^[a-f\d]{64}$/i.test(provenance.selectedParcelHash as string)) {
        errors.push('exhibit.provenance.selectedParcelHash must be a SHA-256 digest or null.');
      }
    }
    if (provenance.frozenAuthority !== undefined) {
      const authority = requiredRecord(
        provenance.frozenAuthority,
        'exhibit.provenance.frozenAuthority',
        errors,
      );
      if (authority) {
        if (authority.contractVersion !== GENERATION_IMPACT_SED_CONTRACT_VERSION) {
          errors.push('exhibit.provenance.frozenAuthority.contractVersion is unsupported.');
        }
        [
          'scenarioId',
          'scenarioUpdatedAt',
          'modelVersion',
          'promotedAt',
          'layoutPublicationId',
          'layoutPublishedAt',
        ].forEach(field => requiredString(
          authority[field],
          `exhibit.provenance.frozenAuthority.${field}`,
          errors,
        ));
        ['scenarioUpdatedAt', 'promotedAt', 'layoutPublishedAt'].forEach(field => {
          const value = authority[field];
          if (typeof value === 'string' && !Number.isFinite(Date.parse(value))) {
            errors.push(`exhibit.provenance.frozenAuthority.${field} must be an ISO timestamp.`);
          }
        });
        ['inputHash', 'resultHash', 'layoutHash', 'boundaryHash'].forEach(field => {
          const value = authority[field];
          if (!requiredString(
            value,
            `exhibit.provenance.frozenAuthority.${field}`,
            errors,
          ) || !/^[a-f\d]{64}$/i.test(String(value))) {
            errors.push(`exhibit.provenance.frozenAuthority.${field} must be a SHA-256 digest.`);
          }
        });
        if (
          !requiredNonnegativeSafeInteger(
            authority.layoutVersion,
            'exhibit.provenance.frozenAuthority.layoutVersion',
            errors,
          )
          || authority.layoutVersion === 0
        ) {
          errors.push('exhibit.provenance.frozenAuthority.layoutVersion must be positive.');
        }
        if (
          !requiredFiniteNumber(
            authority.boundaryAreaAcres,
            'exhibit.provenance.frozenAuthority.boundaryAreaAcres',
            errors,
          )
          || Number(authority.boundaryAreaAcres) <= 0
        ) {
          errors.push('exhibit.provenance.frozenAuthority.boundaryAreaAcres must be positive.');
        }
        const hasSedId = authority.sedId !== undefined;
        const hasSedVersion = authority.sedVersion !== undefined;
        if (hasSedId !== hasSedVersion) {
          errors.push('exhibit.provenance.frozenAuthority SED id and version must be supplied together.');
        } else if (hasSedId && hasSedVersion) {
          requiredString(
            authority.sedId,
            'exhibit.provenance.frozenAuthority.sedId',
            errors,
          );
          if (
            !requiredNonnegativeSafeInteger(
              authority.sedVersion,
              'exhibit.provenance.frozenAuthority.sedVersion',
              errors,
            )
            || authority.sedVersion === 0
          ) {
            errors.push('exhibit.provenance.frozenAuthority.sedVersion must be positive.');
          }
        }
      }
    }
  }

  const overlays = requiredRecord(source.overlays, 'exhibit.overlays', errors);
  if (!overlays) return;
  if (requiredNonnegativeSafeInteger(overlays.maxBytes, 'exhibit.overlays.maxBytes', errors) && overlays.maxBytes === 0) {
    errors.push('exhibit.overlays.maxBytes must be greater than zero.');
  }
  requiredNonnegativeSafeInteger(overlays.serializedBytes, 'exhibit.overlays.serializedBytes', errors);
  requiredNonnegativeSafeInteger(overlays.totalVertices, 'exhibit.overlays.totalVertices', errors);
  requiredStringArray(overlays.sourceLayerIds, 'exhibit.overlays.sourceLayerIds', errors);
  requiredStringArray(overlays.includedLayerIds, 'exhibit.overlays.includedLayerIds', errors);
  requiredStringArray(overlays.omittedLayerIds, 'exhibit.overlays.omittedLayerIds', errors);
  requiredStringArray(overlays.invalidLayerIds, 'exhibit.overlays.invalidLayerIds', errors);
  const features = requiredArray(overlays.features, 'exhibit.overlays.features', errors);
  features?.forEach((feature, index) => validateOverlayFeature(feature, index, errors));
  const cadSources = requiredArray(overlays.cadSources, 'exhibit.overlays.cadSources', errors);
  cadSources?.forEach((source, index) => validateCadSource(source, index, errors));
}

function validateCrossFieldInvariants(
  source: Record<string, unknown>,
  errors: string[],
): void {
  const scenario = isRecord(source.scenario) ? source.scenario : null;
  const engineering = isRecord(source.engineering) ? source.engineering : null;
  const sizingStudy = engineering && isRecord(engineering.sizingStudy)
    ? engineering.sizingStudy
    : null;
  const sizingBasis = sizingStudy && isRecord(sizingStudy.basis) ? sizingStudy.basis : null;
  if (scenario && sizingBasis && (
    sizingBasis.baselineInputHash !== scenario.inputHash
    || sizingBasis.baselineResultHash !== scenario.resultHash
  )) {
    errors.push('Generation sizing study baseline hashes do not match the frozen exhibit scenario.');
  }

  const provenance = isRecord(source.provenance) ? source.provenance : null;
  const frozenAuthority = provenance && isRecord(provenance.frozenAuthority)
    ? provenance.frozenAuthority
    : null;
  if (scenario && frozenAuthority && (
    frozenAuthority.scenarioId !== scenario.id
    || frozenAuthority.scenarioUpdatedAt !== scenario.updatedAt
    || frozenAuthority.inputHash !== scenario.inputHash
    || frozenAuthority.resultHash !== scenario.resultHash
    || frozenAuthority.modelVersion !== scenario.modelVersion
  )) {
    errors.push('Generation layout exhibit frozen authority does not match its scenario identity.');
  }
  const overlays = isRecord(source.overlays) ? source.overlays : null;
  const sourceLayerIds = Array.isArray(overlays?.sourceLayerIds) ? overlays.sourceLayerIds : null;
  if (provenance && sourceLayerIds) {
    if (provenance.visibleLayerCount !== sourceLayerIds.length) {
      errors.push('Generation layout exhibit visibleLayerCount does not match sourceLayerIds.');
    }
    if (
      typeof provenance.sourceLayerCount === 'number'
      && typeof provenance.visibleLayerCount === 'number'
      && provenance.sourceLayerCount < provenance.visibleLayerCount
    ) {
      errors.push('Generation layout exhibit sourceLayerCount cannot be less than visibleLayerCount.');
    }
  }

  const fit = isRecord(source.fit) ? source.fit : null;
  const parcel = fit && isRecord(fit.parcel) ? fit.parcel : null;
  if (parcel) {
    try {
      const geometryHash = hashCanonicalJson({
        id: parcel.id,
        polygon: parcel.polygon,
        area: parcel.areaAcres,
        perimeter: parcel.perimeterKm,
        centroid: parcel.centroid,
      });
      if (parcel.geometryHash !== geometryHash) {
        errors.push('Generation layout exhibit selected parcel geometryHash is stale.');
      }
    } catch (error) {
      errors.push(`Generation layout exhibit selected parcel cannot be hashed: ${(error as Error).message}`);
    }
    if (
      provenance
      && (provenance.selectedParcelId !== parcel.id || provenance.selectedParcelHash !== parcel.geometryHash)
    ) {
      errors.push('Generation layout exhibit selected parcel provenance does not match the frozen fit parcel.');
    }
  } else if (
    provenance
    && (provenance.selectedParcelId !== null || provenance.selectedParcelHash !== null)
  ) {
    errors.push('Generation layout exhibit records selected parcel provenance without a frozen fit parcel.');
  }

  if (fit && Array.isArray(fit.components)) {
    const fitComponents = fit.components;
    const ids = fitComponents.flatMap(component => (
      isRecord(component) && typeof component.id === 'string' ? [component.id] : []
    ));
    if (ids.length !== new Set(ids).size) {
      errors.push('Generation layout exhibit fit component ids must be unique.');
    }
    if (fit.fitModelVersion === 'generation-test-fit-v2') {
      const plan = isRecord(fit.plan) ? fit.plan : null;
      const summary = plan && isRecord(plan.reservationSummary)
        ? plan.reservationSummary
        : null;
      if (!summary) {
        errors.push('generation-test-fit-v2 requires a reservationSummary.');
      } else {
        const componentArea = (
          lifecycle: 'permanent' | 'route-reservation' | 'construction'
        ) => fitComponents.reduce<number>((total, component) => (
          isRecord(component)
          && component.lifecycle === lifecycle
          && typeof component.areaAcres === 'number'
            ? total + component.areaAcres
            : total
        ), 0);
        const permanent = componentArea('permanent');
        const routes = componentArea('route-reservation');
        const construction = componentArea('construction');
        if (
          !approximatelyEqual(Number(summary.permanentEquipmentAcres), permanent)
          || !approximatelyEqual(Number(summary.routeReservationAcres), routes)
          || !approximatelyEqual(Number(summary.constructionLaydownAcres), construction)
          || !approximatelyEqual(
            Number(summary.totalReservedAcres),
            permanent + routes + construction,
          )
        ) {
          errors.push('Generation layout exhibit reservationSummary does not match its frozen components.');
        }
        const recordedTotal = isRecord(plan)
          && typeof plan.totalComponentAreaAcres === 'number'
          ? plan.totalComponentAreaAcres
          : Number.NaN;
        if (!approximatelyEqual(recordedTotal, permanent + routes + construction)) {
          errors.push('Generation layout exhibit totalComponentAreaAcres does not match its frozen components.');
        }
      }
      fitComponents.forEach((component, index) => {
        if (!isRecord(component)) return;
        if (
          typeof component.areaAcres === 'number'
          && typeof component.widthM === 'number'
          && typeof component.heightM === 'number'
          // User-defined (studio) shapes freeze true polygon area with a
          // bounding-box width/height, so the rectangle identity only binds
          // the packer-generated bases.
          && component.dimensionBasis !== 'user-defined'
          && !approximatelyEqual(
            component.areaAcres,
            component.widthM * component.heightM / SQUARE_METRES_PER_ACRE,
          )
        ) {
          errors.push(`Generation layout exhibit fit component ${index} area does not match its dimensions.`);
        }
        const expectedLifecycle = component.kind === 'natural-gas-route'
          || component.kind === 'co2-export-route'
          ? 'route-reservation'
          : component.kind === 'construction-laydown'
            ? 'construction'
            : 'permanent';
        if (component.lifecycle !== expectedLifecycle) {
          errors.push(`Generation layout exhibit fit component ${index} lifecycle does not match its kind.`);
        }
      });
    }
  }
}

/**
 * One fail-closed compatibility gate shared by viewer and export surfaces.
 * It validates immutable bytes, schema, scenario identity, overlay completeness,
 * CAD digest provenance and every finite numeric value (including geometry).
 */
export function validateGenerationLayoutExhibitSnapshot(
  candidate: unknown,
  alignedImpact?: GenerationImpactSnapshot | null,
): GenerationLayoutExhibitValidationResult {
  const errors: string[] = [];
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return { valid: false, errors: ['Generation layout exhibit is not an object.'] };
  }
  const source = candidate as Record<string, unknown>;
  if (source.schemaVersion !== GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION) {
    errors.push(`Unsupported generation layout exhibit schema version: ${String(source.schemaVersion)}.`);
  }
  if (source.modelVersion !== GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION) {
    errors.push(`Unsupported generation layout projection model version: ${String(source.modelVersion)}.`);
  }
  if (typeof source.capturedAt !== 'string' || !Number.isFinite(Date.parse(source.capturedAt))) {
    errors.push('Generation layout exhibit capturedAt is not a valid ISO timestamp.');
  }
  const contentHash = source.contentHash;
  if (typeof contentHash !== 'string' || !/^[a-f\d]{64}$/i.test(contentHash)) {
    errors.push('Generation layout exhibit contentHash is not a SHA-256 digest.');
  } else {
    try {
      const { contentHash: _ignored, ...semanticSnapshot } = source;
      if (hashCanonicalJson(semanticSnapshot) !== contentHash) {
        errors.push('Generation layout exhibit contentHash does not match its semantic payload.');
      }
    } catch (error) {
      errors.push(`Generation layout exhibit payload cannot be hashed: ${(error as Error).message}`);
    }
  }

  const scenario = source.scenario as Record<string, unknown> | undefined;
  for (const field of ['id', 'name', 'inputHash', 'resultHash', 'hashVersion', 'modelVersion', 'updatedAt'] as const) {
    if (typeof scenario?.[field] !== 'string' || scenario[field].length === 0) {
      errors.push(`Generation layout exhibit scenario.${field} is missing.`);
    }
  }
  for (const digestField of ['inputHash', 'resultHash'] as const) {
    if (typeof scenario?.[digestField] === 'string' && !/^[a-f\d]{64}$/i.test(scenario[digestField])) {
      errors.push(`Generation layout exhibit scenario.${digestField} is not a SHA-256 digest.`);
    }
  }
  if (scenario?.modelVersion !== 'generation-impact-v1') {
    errors.push(`Unsupported generation impact model version in exhibit: ${String(scenario?.modelVersion)}.`);
  }
  if (scenario?.hashVersion !== GENERATION_IMPACT_HASH_VERSION) {
    errors.push(`Unsupported generation impact hash version in exhibit: ${String(scenario?.hashVersion)}.`);
  }
  if (
    typeof scenario?.updatedAt === 'string'
    && !Number.isFinite(Date.parse(scenario.updatedAt))
  ) {
    errors.push('Generation layout exhibit scenario.updatedAt is not a valid timestamp.');
  }
  if (alignedImpact) {
    const aligned = alignedImpact.scenario;
    const integrity = validateGenerationImpactScenarioIntegrity(aligned);
    errors.push(...integrity.errors.map(error => `generationImpactAtGen integrity: ${error}`));
    if (
      scenario?.id !== aligned.id
      || scenario.name !== aligned.name
      || scenario.inputHash !== aligned.inputHash
      || scenario.resultHash !== aligned.resultHash
      || scenario.hashVersion !== (aligned.hashVersion ?? GENERATION_IMPACT_HASH_VERSION)
      || scenario.modelVersion !== aligned.result.modelVersion
      || scenario.updatedAt !== aligned.updatedAt
      || source.capturedAt !== alignedImpact.capturedAt
    ) {
      errors.push('Generation layout exhibit scenario identity does not match generationImpactAtGen.');
    }
    try {
      const sourceEngineering = isRecord(source.engineering) ? source.engineering : null;
      const includeSizingStudy = Boolean(sourceEngineering && 'sizingStudy' in sourceEngineering);
      const sourceStudy = sourceEngineering && isRecord(sourceEngineering.sizingStudy)
        ? sourceEngineering.sizingStudy
        : null;
      const includeContingencyGranularity = Boolean(sourceStudy && 'contingencyGranularity' in sourceStudy);
      if (canonicalJson(source.engineering) !== canonicalJson(
        engineeringSchedule(alignedImpact, includeSizingStudy, includeContingencyGranularity),
      )) {
        errors.push('Generation layout exhibit engineering schedule does not match generationImpactAtGen.');
      }
    } catch (error) {
      errors.push(`generationImpactAtGen cannot be projected into an engineering schedule: ${(error as Error).message}`);
    }
    try {
      const recordedFit = isRecord(source.fit) ? source.fit : null;
      if (recordedFit?.fitModelVersion === 'generation-test-fit-v2') {
        const fitParcel = isRecord(recordedFit.parcel) ? recordedFit.parcel : null;
        const parcels: SiteBoundaryParcel[] = fitParcel
          ? [{
              id: String(fitParcel.id),
              ...(typeof fitParcel.label === 'string' ? { label: fitParcel.label } : {}),
              polygon: jsonClone(fitParcel.polygon) as SiteBoundaryParcel['polygon'],
              area: Number(fitParcel.areaAcres),
              perimeter: Number(fitParcel.perimeterKm),
              centroid: jsonClone(fitParcel.centroid) as SiteBoundaryParcel['centroid'],
              ...(typeof fitParcel.intendedUse === 'string'
                ? { intendedUse: fitParcel.intendedUse as SiteBoundaryParcel['intendedUse'] }
                : {}),
            }]
          : [];
        const recordedAnchor = jsonClone(recordedFit.anchor) as { lat: number; lng: number };
        if (canonicalJson(source.fit) !== canonicalJson(
          frozenFit(alignedImpact, parcels, recordedAnchor),
        )) {
          errors.push('Generation layout exhibit installed-reservation fit does not match generationImpactAtGen.');
        }
      }
    } catch (error) {
      errors.push(`generationImpactAtGen cannot be projected into an installed-reservation fit: ${(error as Error).message}`);
    }
  }

  validateTopLevelStructure(source, errors);
  validateCrossFieldInvariants(source, errors);
  collectNonFiniteNumbers(source, 'exhibit', errors);
  const overlays = source.overlays as Record<string, unknown> | undefined;
  const overlayRecord = overlays ?? {};
  const status = overlays?.status;
  const features = Array.isArray(overlays?.features) ? overlays.features as unknown[] : null;
  const cadSources = Array.isArray(overlays?.cadSources) ? overlays.cadSources as unknown[] : null;
  const sourceLayerIds = Array.isArray(overlays?.sourceLayerIds)
    ? overlays.sourceLayerIds.filter((value): value is string => typeof value === 'string')
    : null;
  const includedLayerIds = Array.isArray(overlays?.includedLayerIds)
    ? overlays.includedLayerIds.filter((value): value is string => typeof value === 'string')
    : null;
  const omittedLayerIds = Array.isArray(overlays?.omittedLayerIds)
    ? overlays.omittedLayerIds.filter((value): value is string => typeof value === 'string')
    : null;
  const invalidLayerIds = Array.isArray(overlays?.invalidLayerIds)
    ? overlays.invalidLayerIds.filter((value): value is string => typeof value === 'string')
    : null;
  if (!features || !cadSources || !sourceLayerIds || !includedLayerIds || !omittedLayerIds || !invalidLayerIds) {
    errors.push('Generation layout exhibit overlay arrays are malformed.');
  } else {
    if (!sortedUnique(sourceLayerIds)) {
      errors.push('Generation layout exhibit sourceLayerIds must be sorted and unique.');
    }
    for (const [name, ids] of [
      ['includedLayerIds', includedLayerIds],
      ['omittedLayerIds', omittedLayerIds],
      ['invalidLayerIds', invalidLayerIds],
    ] as const) {
      if (!sortedUnique(ids)) {
        errors.push(`Generation layout exhibit ${name} must be sorted and unique.`);
      }
    }
    const featureIds = features
      .map(feature => isRecord(feature) ? feature.id : undefined)
      .filter((id): id is string => typeof id === 'string');
    if (featureIds.length !== features.length) {
      errors.push('Generation layout exhibit contains a feature without a string id.');
    }
    const invalidCadDigest = [
      ...features.flatMap(feature => (
        isRecord(feature) && feature.cad != null ? [feature.cad] : []
      )),
      ...cadSources,
    ].some((entry) => {
      const digest = isRecord(entry) ? entry.sourceSha256 : undefined;
      return typeof digest !== 'string' || !/^[a-f\d]{64}$/i.test(digest);
    });
    if (invalidCadDigest) {
      errors.push('Generation layout exhibit contains CAD provenance without a full SHA-256 digest.');
    }
    const cadSourceDigests = cadSources.flatMap(source => (
      isRecord(source) && typeof source.sourceSha256 === 'string' ? [source.sourceSha256] : []
    ));
    if (cadSourceDigests.length !== cadSources.length || !sortedUnique(cadSourceDigests)) {
      errors.push('Generation layout exhibit CAD sources must be sorted and unique by SHA-256.');
    }
    const recordedCadFeatureCount = cadSources.reduce<number>((total, source) => (
      isRecord(source) && typeof source.featureCount === 'number'
        ? total + source.featureCount
        : total
    ), 0);
    if (recordedCadFeatureCount > sourceLayerIds.length) {
      errors.push('Generation layout exhibit CAD feature count exceeds the visible source layer set.');
    }
    if (status === 'included') {
      if (!sameStrings(featureIds, sourceLayerIds) || !sameStrings(includedLayerIds, sourceLayerIds)) {
        errors.push('Included generation overlay ids do not exactly match the complete source layer set.');
      }
      if (omittedLayerIds.length > 0) {
        errors.push('Included generation overlay unexpectedly lists omitted layer ids.');
      }
      if (invalidLayerIds.length > 0) {
        errors.push('Included generation overlay unexpectedly lists invalid layer ids.');
      }
      const reconstructedCadSources = reconstructCadSourcesFromFeatures(features, errors);
      if (canonicalJson(reconstructedCadSources) !== canonicalJson(cadSources)) {
        errors.push('Included generation overlay CAD source aggregate does not match its CAD features.');
      }
      const actualVertices = features.reduce((total: number, feature: unknown): number => {
        if (!isRecord(feature) || !isRecord(feature.geometry) || !Array.isArray(feature.geometry.coordinates)) {
          return total;
        }
        return total + feature.geometry.coordinates.length;
      }, 0);
      if (overlayRecord.totalVertices !== actualVertices) {
        errors.push('Included generation overlay totalVertices does not match its frozen geometry.');
      }
      const actualBytes = new TextEncoder().encode(canonicalJson({ features, cadSources })).byteLength;
      if (overlayRecord.serializedBytes !== actualBytes) {
        errors.push('Generation layout exhibit serialized overlay byte count is stale.');
      }
      if (
        typeof overlayRecord.maxBytes !== 'number'
        || actualBytes > overlayRecord.maxBytes
      ) {
        errors.push('Included generation overlay exceeds its recorded byte ceiling.');
      }
    } else if (status === 'omitted-too-large') {
      if (
        features.length > 0
        || includedLayerIds.length > 0
        || invalidLayerIds.length > 0
        || !sameStrings(omittedLayerIds, sourceLayerIds)
      ) {
        errors.push('Oversize generation overlay must omit the complete layer set without a retained prefix.');
      }
      if (
        typeof overlayRecord.serializedBytes !== 'number'
        || typeof overlayRecord.maxBytes !== 'number'
        || overlayRecord.serializedBytes <= overlayRecord.maxBytes
      ) {
        errors.push('Oversize generation overlay byte evidence does not exceed its ceiling.');
      }
    } else if (status === 'omitted-invalid') {
      const invalidIdSet = new Set(invalidLayerIds);
      if (
        features.length > 0
        || includedLayerIds.length > 0
        || invalidLayerIds.length === 0
        || !invalidLayerIds.every(id => sourceLayerIds.includes(id))
        || invalidIdSet.size !== invalidLayerIds.length
        || !sameStrings(omittedLayerIds, sourceLayerIds)
      ) {
        errors.push('Invalid generation overlay must identify offending ids and omit the complete layer set.');
      }
      if (overlayRecord.serializedBytes !== 0) {
        errors.push('Invalid generation overlay must record zero serialized geometry bytes.');
      }
    } else {
      errors.push(`Unsupported generation layout overlay status: ${String(status)}.`);
    }
  }
  return { valid: errors.length === 0, errors };
}

/** Throwing form for export/rasterization boundaries. */
export function requireValidGenerationLayoutExhibitSnapshot(
  candidate: unknown,
  alignedImpact?: GenerationImpactSnapshot | null,
): GenerationLayoutExhibitSnapshot {
  const validation = validateGenerationLayoutExhibitSnapshot(candidate, alignedImpact);
  if (!validation.valid) {
    throw new TypeError(`Invalid generation layout exhibit: ${validation.errors.join(' ')}`);
  }
  return candidate as GenerationLayoutExhibitSnapshot;
}
